/**
 * Talent Tree Data Definitions
 *
 * Each skill has its own tree with:
 * - Unique visual shape (pickaxe, potion, tree, etc.)
 * - Nodes with effects, prerequisites, and positions
 * - Connection definitions for rendering lines
 *
 * Effect Types:
 * - xpBonus: Multiplicative XP bonus (0.05 = +5%)
 * - speedBonus: Multiplicative speed bonus (0.05 = +5% faster)
 * - lootBonus: Multiplicative loot quantity bonus
 * - procChance: Chance to trigger special effect
 * - statBonus: Flat stat increase (for combat)
 */

import { SKILLS } from '../../game/constants.js';

// Node state constants
export const NODE_STATES = {
    LOCKED: 'locked',
    AVAILABLE: 'available',
    PURCHASED: 'purchased',
    MAXED: 'maxed'
};

/**
 * Mining Talent Tree
 * Shape: Pickaxe - handle at bottom, blade spreads at top
 * 12 nodes, all obtainable by level 90 (1 point per 7.5 levels)
 *
 * Layout:
 *     [PureVein]     [MasterMiner]     [Motherlode]
 *          \              |              /
 *   [GemFinder] [Efficient] [RichVeins] [Prospector]
 *           \       |           |       /
 *      [SwiftStrikes] [ToolMastery] [KeenEye]
 *               \         |         /
 *                   [Foundation]
 */
/**
 * Mining Talent Tree (Reworked)
 * Shape: Pickaxe - foundation at bottom, three distinct branches upward
 * Theme: Three paths to mining mastery
 *
 * Branches:
 * - LEFT (Prospector's Path): Finding valuables - gems, rare drops
 * - CENTER (Foreman's Path): Efficiency - tools, speed, consistency
 * - RIGHT (Deep Miner's Path): Big hauls - quantity, jackpots
 *
 * Layout:
 *                    [Heart of the Mountain]
 *                   /          |          \
 *       [GrandProspector] [PitBoss]  [Motherlode]
 *              |              |            |
 *        [GemBonanza]  [SteadyOutput] [PureVein]
 *              |              |            |
 *        [RareEarth]   [SwiftStrikes] [RichVeins]
 *              |              |            |
 *        [GemFinder]   [ToolMastery]  [KeenEye]
 *               \             |           /
 *                    [Foundation]
 */
export const MINING_TREE = {
    id: SKILLS.MINING,
    name: 'Mining',
    shape: 'pickaxe',
    canvasSize: { width: 700, height: 550 },
    backgroundColor: '#1a1a2e',
    accentColor: '#b87333', // Copper

    // 6-tier tree with 3 distinct branches + super-capstone
    // Total ranks: 3 + 6 + 5 + 3 + 3 + 1 = 21
    totalRanks: 21,

    nodes: {
        // ==================== TIER 1 - FOUNDATION ====================
        'mining_foundation': {
            id: 'mining_foundation',
            name: "Miner's Foundation",
            description: '+5% Mining XP per rank',
            detailedDescription: 'The cornerstone of mining mastery. Each rank increases the experience you gain from mining any rock.',
            position: { x: 350, y: 480 },
            maxRank: 3,
            effects: [
                { type: 'xpBonus', value: 0.05, perRank: true }
            ],
            prerequisites: [],
            tier: 1
        },

        // ==================== TIER 2 - THREE PATHS BEGIN ====================
        // LEFT: Prospector's Path - Finding valuables
        'mining_gems': {
            id: 'mining_gems',
            name: 'Gem Finder',
            description: '+5% gem drop chance per rank',
            detailedDescription: 'You notice the glint of precious stones others would miss. Chance to find gems while mining any rock. Higher-tier gems unlock at higher mining levels and are rarer. <span class="pet-synergy"><span class="husbandry-icon"></span> Your pets can also find gems on their expeditions.</span>',
            position: { x: 180, y: 390 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'gemDrop', value: 0.05, perRank: true }
            ],
            prerequisites: ['mining_foundation'],
            tier: 2
        },
        // CENTER: Foreman's Path - Efficiency
        'mining_tools': {
            id: 'mining_tools',
            name: 'Tool Mastery',
            description: '+10% tool effectiveness per rank',
            detailedDescription: 'You learn to get more out of your pickaxe. Amplifies the speed bonus provided by your equipped pickaxe.',
            position: { x: 350, y: 390 },
            maxRank: 2,
            effects: [
                { type: 'toolBonus', value: 0.10, perRank: true }
            ],
            prerequisites: ['mining_foundation'],
            tier: 2
        },
        // RIGHT: Deep Miner's Path - Big hauls
        'mining_keen': {
            id: 'mining_keen',
            name: 'Keen Eye',
            description: '+8% double ore chance per rank',
            detailedDescription: 'Your trained eye spots the best veins. Each mining action has a chance to yield double ore.',
            position: { x: 520, y: 390 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'doubleOre', value: 0.08, perRank: true }
            ],
            prerequisites: ['mining_foundation'],
            tier: 2
        },

        // ==================== TIER 3 - BRANCH DEVELOPMENT ====================
        'mining_rare': {
            id: 'mining_rare',
            name: 'Rare Earth',
            description: '10% chance for bonus ore type',
            detailedDescription: 'Experience teaches you that veins often contain more than one mineral. Chance to receive a random secondary ore when mining. <span class="pet-synergy"><span class="husbandry-icon"></span> Your pets can bring back random ores.</span>',
            position: { x: 180, y: 300 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'bonusOre', value: 0.10, perRank: false }
            ],
            prerequisites: ['mining_gems'],
            tier: 3
        },
        'mining_swift': {
            id: 'mining_swift',
            name: 'Swift Strikes',
            description: '-8% mining time per rank',
            detailedDescription: 'Your pickaxe swings become faster and more fluid. Reduces the time it takes to mine each rock.',
            position: { x: 350, y: 300 },
            maxRank: 2,
            effects: [
                { type: 'speedBonus', value: 0.08, perRank: true }
            ],
            prerequisites: ['mining_tools'],
            tier: 3
        },
        'mining_rich': {
            id: 'mining_rich',
            name: 'Rich Veins',
            description: '+12% ore quantity per rank',
            detailedDescription: 'You extract more material from each rock. Increases the base amount of ore received from mining.',
            position: { x: 520, y: 300 },
            maxRank: 2,
            effects: [
                { type: 'lootBonus', value: 0.12, perRank: true }
            ],
            prerequisites: ['mining_keen'],
            tier: 3
        },

        // ==================== TIER 4 - PRE-CAPSTONES ====================
        'mining_bonanza': {
            id: 'mining_bonanza',
            name: 'Gem Bonanza',
            description: 'Gem procs give 2 gems instead of 1',
            detailedDescription: 'When you find gems, you find them in pairs. All gem drop procs now yield two gems instead of one.',
            position: { x: 180, y: 210 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'gemQuantity', value: 1.0, perRank: false }
            ],
            prerequisites: ['mining_rare'],
            tier: 4
        },
        'mining_steady': {
            id: 'mining_steady',
            name: 'Steady Output',
            description: '+1% speed per consecutive mine (max 15%)',
            detailedDescription: 'You build momentum as you work. Each consecutive mining action on the same rock type increases your speed, up to 15% bonus.',
            position: { x: 350, y: 210 },
            maxRank: 1,
            effects: [
                { type: 'stackingSpeed', value: 0.01, maxStacks: 0.15, perRank: false }
            ],
            prerequisites: ['mining_swift'],
            tier: 4
        },
        'mining_purevein': {
            id: 'mining_purevein',
            name: 'Pure Vein',
            description: '5% chance for 5x ore',
            detailedDescription: 'Sometimes you strike a vein of exceptional purity. Rare chance to receive five times the normal ore from a single action.',
            position: { x: 520, y: 210 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'pureVein', value: 0.05, perRank: false }
            ],
            prerequisites: ['mining_rich'],
            tier: 4
        },

        // ==================== TIER 5 - CAPSTONES ====================
        'mining_prospector': {
            id: 'mining_prospector',
            name: 'Grand Prospector',
            description: '+25% all drop chances, +10% gem chance',
            detailedDescription: 'Your prospecting skills are legendary. All proc chances for bonus drops are increased by 25%, and you gain an additional flat 10% chance to find gems.',
            position: { x: 180, y: 120 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'dropChanceBonus', value: 0.25, perRank: false },
                { type: 'flatBonus', bonus: 'gemDropFlat', value: 0.10, perRank: false }
            ],
            prerequisites: ['mining_bonanza'],
            tier: 5
        },
        'mining_pitboss': {
            id: 'mining_pitboss',
            name: 'Pit Boss',
            description: 'All mining bonuses +20%, +10% stack cap',
            detailedDescription: 'You run the most efficient mining operation around. All mining talent bonuses are amplified by 20%, and your Steady Output stacking cap is increased by 10%.',
            position: { x: 350, y: 120 },
            maxRank: 1,
            effects: [
                { type: 'globalBonus', value: 0.20, perRank: false },
                { type: 'flatBonus', bonus: 'stackingCapBonus', value: 0.10, perRank: false }
            ],
            prerequisites: ['mining_steady'],
            tier: 5
        },
        'mining_motherlode': {
            id: 'mining_motherlode',
            name: 'Motherlode',
            description: '5% for 10x ore, 1% for 25x jackpot',
            detailedDescription: 'Legends speak of veins so rich they seem endless. 5% chance to hit a motherlode for 10x ore, and 1% chance for a massive 25x jackpot.',
            position: { x: 520, y: 120 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'motherlode', value: 0.05, perRank: false },
                { type: 'procChance', proc: 'jackpot', value: 0.01, perRank: false }
            ],
            prerequisites: ['mining_purevein'],
            tier: 5
        },

        // ==================== TIER 6 - SUPER-CAPSTONE ====================
        'mining_heart': {
            id: 'mining_heart',
            name: 'Heart of the Mountain',
            description: '15% chance to auto-smelt ore into bars',
            detailedDescription: 'Your understanding of metals transcends mining itself. 15% chance when mining to also receive the smelted bar and Smithing XP. Requires sufficient Smithing level for that bar type.',
            position: { x: 350, y: 40 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'autoSmelt', value: 0.15, perRank: false }
            ],
            prerequisites: ['mining_prospector', 'mining_pitboss', 'mining_motherlode'],
            requirePrerequisites: 2, // Only need 2 of 3 capstones
            tier: 6
        }
    },

    // Connection lines between nodes
    connections: [
        // Tier 1 to Tier 2
        { from: 'mining_foundation', to: 'mining_gems', style: 'straight' },
        { from: 'mining_foundation', to: 'mining_tools', style: 'straight' },
        { from: 'mining_foundation', to: 'mining_keen', style: 'straight' },

        // Tier 2 to Tier 3 (each branch stays separate)
        { from: 'mining_gems', to: 'mining_rare', style: 'straight' },
        { from: 'mining_tools', to: 'mining_swift', style: 'straight' },
        { from: 'mining_keen', to: 'mining_rich', style: 'straight' },

        // Tier 3 to Tier 4
        { from: 'mining_rare', to: 'mining_bonanza', style: 'straight' },
        { from: 'mining_swift', to: 'mining_steady', style: 'straight' },
        { from: 'mining_rich', to: 'mining_purevein', style: 'straight' },

        // Tier 4 to Tier 5 (Capstones)
        { from: 'mining_bonanza', to: 'mining_prospector', style: 'straight' },
        { from: 'mining_steady', to: 'mining_pitboss', style: 'straight' },
        { from: 'mining_purevein', to: 'mining_motherlode', style: 'straight' },

        // Tier 5 to Tier 6 (Super-capstone - requires 2 of 3)
        { from: 'mining_prospector', to: 'mining_heart', style: 'straight' },
        { from: 'mining_pitboss', to: 'mining_heart', style: 'straight' },
        { from: 'mining_motherlode', to: 'mining_heart', style: 'straight' }
    ]
};

/**
 * Smithing Talent Tree (Reworked) - "Heat & Efficiency"
 * Shape: Anvil - foundation at bottom, spreading upward like sparks
 * 13 nodes, 21 total ranks, 5 tiers
 *
 * Branches:
 * - LEFT (Forgemaster): Speed, forge efficiency, ash generation
 * - CENTER (Fletcher's Path): Resource conservation, arrowhead production
 * - RIGHT (Armorer's Path): Output quantity, equipment crafting
 *
 * Layout:
 *       [Furnace]      [Fletcher's]     [Bulk]
 *            |              |              |
 *     [ForgeMomentum]  [Arrowsmith]  [EquipExpert]
 *            |              |              |
 *     [HeatRetention]  [Resource]   [Tempered]
 *            |              |              |
 *      [SwiftHammer]     [Tool]    [EfficientFuel]
 *               \          |          /
 *                  [Foundation]
 */
export const SMITHING_TREE = {
    id: SKILLS.SMITHING,
    name: 'Smithing',
    shape: 'anvil',
    canvasSize: { width: 600, height: 550 },
    backgroundColor: '#1a1a2e',
    accentColor: '#ff6b35', // Forge orange

    totalRanks: 21,

    nodes: {
        // ==================== TIER 1 - FOUNDATION ====================
        'smithing_foundation': {
            id: 'smithing_foundation',
            name: "Smith's Foundation",
            description: '+5% Smithing XP per rank',
            detailedDescription: 'Master the fundamentals of the forge. Each rank increases experience gained from all smithing activities.',
            position: { x: 300, y: 480 },
            maxRank: 3,
            effects: [
                { type: 'xpBonus', value: 0.05, perRank: true }
            ],
            prerequisites: [],
            tier: 1
        },

        // ==================== TIER 2 - THREE PATHS BEGIN ====================
        'smithing_swift': {
            id: 'smithing_swift',
            name: 'Swift Hammer',
            description: '-5% smithing time per rank',
            detailedDescription: 'Your hammer strikes become more precise and rhythmic. Reduces the time to complete any smithing action.',
            position: { x: 150, y: 390 },
            maxRank: 2,
            effects: [
                { type: 'speedBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['smithing_foundation'],
            tier: 2
        },
        'smithing_tools': {
            id: 'smithing_tools',
            name: 'Tool Mastery',
            description: '+10% forge effectiveness per rank',
            detailedDescription: 'You understand how to get the most from your forge. Amplifies the speed bonus from your equipped forge.',
            position: { x: 300, y: 390 },
            maxRank: 2,
            effects: [
                { type: 'toolBonus', value: 0.10, perRank: true }
            ],
            prerequisites: ['smithing_foundation'],
            tier: 2
        },
        'smithing_fuel': {
            id: 'smithing_fuel',
            name: 'Efficient Fuel',
            description: '8% chance to save coal per rank',
            detailedDescription: 'You learn to maintain forge temperature with less fuel. Chance to not consume coal when smelting bars.',
            position: { x: 450, y: 390 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'coalSave', value: 0.08, perRank: true }
            ],
            prerequisites: ['smithing_foundation'],
            tier: 2
        },

        // ==================== TIER 3 - SPECIALIZATION ====================
        'smithing_heat': {
            id: 'smithing_heat',
            name: 'Heat Retention',
            description: '+3% speed per consecutive craft (15% max)',
            detailedDescription: 'The forge stays hot between crafts. Each consecutive craft of the same item is faster, up to 15% bonus. Resets when you switch items or stop.',
            position: { x: 150, y: 300 },
            maxRank: 2,
            effects: [
                { type: 'stackingSpeed', proc: 'heatRetention', value: 0.03, maxStacks: 0.15, perRank: true }
            ],
            prerequisites: ['smithing_swift'],
            tier: 3
        },
        'smithing_resource': {
            id: 'smithing_resource',
            name: 'Resource Saver',
            description: '5% chance to save materials per rank',
            detailedDescription: 'Waste not, want not. Chance to complete a craft without consuming the input materials.',
            position: { x: 300, y: 300 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'resourceSave', value: 0.05, perRank: true }
            ],
            prerequisites: ['smithing_tools'],
            tier: 3
        },
        'smithing_tempered': {
            id: 'smithing_tempered',
            name: 'Tempered Output',
            description: '+10% smithing output per rank',
            detailedDescription: 'Your refined technique extracts more from each smelt. Increases the quantity of bars and items produced.',
            position: { x: 450, y: 300 },
            maxRank: 2,
            effects: [
                { type: 'lootBonus', value: 0.10, perRank: true }
            ],
            prerequisites: ['smithing_fuel'],
            tier: 3
        },

        // ==================== TIER 4 - ADVANCED ====================
        'smithing_momentum': {
            id: 'smithing_momentum',
            name: 'Forge Momentum',
            description: '5% chance for instant next craft',
            detailedDescription: 'When the forge is roaring, sometimes everything aligns perfectly. Chance for your next smithing action to complete instantly.',
            position: { x: 150, y: 210 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'forgeInstant', value: 0.05, perRank: false }
            ],
            prerequisites: ['smithing_heat'],
            tier: 4
        },
        'smithing_arrowsmith': {
            id: 'smithing_arrowsmith',
            name: 'Arrowsmith',
            description: '+20% arrowhead output',
            detailedDescription: 'You specialize in crafting arrowheads with precision. All arrowhead crafting produces 20% more output.',
            position: { x: 300, y: 210 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'arrowheadOutput', value: 0.20, perRank: false }
            ],
            prerequisites: ['smithing_resource'],
            tier: 4
        },
        'smithing_equipment': {
            id: 'smithing_equipment',
            name: 'Equipment Expertise',
            description: '+15% XP when crafting equipment',
            detailedDescription: 'Your expertise in forging weapons and armor grants bonus experience when crafting any equipment piece.',
            position: { x: 450, y: 210 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'equipmentXPBonus', value: 0.15, perRank: false }
            ],
            prerequisites: ['smithing_tempered'],
            tier: 4
        },

        // ==================== TIER 5 - CAPSTONES ====================
        'smithing_furnace': {
            id: 'smithing_furnace',
            name: 'Furnace Mastery',
            description: 'Generate ash while smithing (tier-scaled)',
            detailedDescription: 'You master the art of capturing forge byproducts. Generate 1 ash per 5 crafts with T1-T3 materials, or 1 ash per 3 crafts with T4-T6 materials. Useful for Glyphbinding and Alchemy.',
            position: { x: 150, y: 120 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'furnaceMastery', value: 1.0, perRank: false }
            ],
            prerequisites: ['smithing_momentum'],
            tier: 5
        },
        'smithing_fletcher': {
            id: 'smithing_fletcher',
            name: "Fletcher's Supply",
            description: '+50% arrowhead output',
            detailedDescription: "Your mastery of arrowhead crafting lets you produce far more from each batch. All arrowhead crafting produces 50% more output. Stacks with Arrowsmith for +70% total.",
            position: { x: 300, y: 120 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'fletcherSupply', value: 0.50, perRank: false }
            ],
            prerequisites: ['smithing_arrowsmith'],
            tier: 5
        },
        'smithing_bulk': {
            id: 'smithing_bulk',
            name: 'Bulk Commission',
            description: '10% chance for extra equipment piece',
            detailedDescription: 'Sometimes you craft so efficiently that you complete two pieces in the time of one. When crafting weapons or armor, 10% chance to receive an extra piece.',
            position: { x: 450, y: 120 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'bulkCommission', value: 0.10, perRank: false }
            ],
            prerequisites: ['smithing_equipment'],
            tier: 5
        }
    },

    connections: [
        // Tier 1 to Tier 2
        { from: 'smithing_foundation', to: 'smithing_swift', style: 'straight' },
        { from: 'smithing_foundation', to: 'smithing_tools', style: 'straight' },
        { from: 'smithing_foundation', to: 'smithing_fuel', style: 'straight' },

        // Tier 2 to Tier 3
        { from: 'smithing_swift', to: 'smithing_heat', style: 'straight' },
        { from: 'smithing_tools', to: 'smithing_resource', style: 'straight' },
        { from: 'smithing_fuel', to: 'smithing_tempered', style: 'straight' },

        // Tier 3 to Tier 4
        { from: 'smithing_heat', to: 'smithing_momentum', style: 'straight' },
        { from: 'smithing_resource', to: 'smithing_arrowsmith', style: 'straight' },
        { from: 'smithing_tempered', to: 'smithing_equipment', style: 'straight' },

        // Tier 4 to Tier 5 (Capstones)
        { from: 'smithing_momentum', to: 'smithing_furnace', style: 'straight' },
        { from: 'smithing_arrowsmith', to: 'smithing_fletcher', style: 'straight' },
        { from: 'smithing_equipment', to: 'smithing_bulk', style: 'straight' }
    ]
};

// ============================================
// WOODCUTTING TALENT TREE - "Forest Keeper"
// 3-Branch Design: Speed Master | Forest Keeper | Master Harvester
// CENTER branch extends one tier deeper than LEFT/RIGHT
// ============================================

export const WOODCUTTING_TREE = {
    id: SKILLS.WOODCUTTING,
    name: 'Woodcutting',
    shape: 'tree',
    canvasSize: { width: 600, height: 600 },
    backgroundColor: '#1a1a2e',
    accentColor: '#228b22', // Forest green

    totalRanks: 22,

    nodes: {
        // === TIER 1: Foundation ===
        'woodcutting_foundation': {
            id: 'woodcutting_foundation',
            name: "Woodcutter's Foundation",
            description: '+5% Woodcutting XP per rank',
            detailedDescription: 'Learn the fundamentals of forestry. Each rank increases experience gained from all woodcutting activities.',
            position: { x: 300, y: 530 },
            maxRank: 3,
            effects: [
                { type: 'xpBonus', value: 0.05, perRank: true }
            ],
            prerequisites: [],
            tier: 1
        },

        // === TIER 2: Three Paths ===
        'woodcutting_swift': {
            id: 'woodcutting_swift',
            name: 'Swift Chop',
            description: '-5% chopping time per rank',
            detailedDescription: 'Your axe swings become more fluid and efficient. Reduces the time to fell any tree.',
            position: { x: 150, y: 430 },
            maxRank: 2,
            effects: [
                { type: 'speedBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['woodcutting_foundation'],
            tier: 2
        },
        'woodcutting_tools': {
            id: 'woodcutting_tools',
            name: 'Axe Mastery',
            description: '+10% axe effectiveness per rank',
            detailedDescription: 'You understand the perfect angle for every swing. Amplifies the speed bonus from your equipped axe.',
            position: { x: 300, y: 430 },
            maxRank: 2,
            effects: [
                { type: 'toolBonus', value: 0.10, perRank: true }
            ],
            prerequisites: ['woodcutting_foundation'],
            tier: 2
        },
        'woodcutting_keen': {
            id: 'woodcutting_keen',
            name: 'Keen Eye',
            description: '+5% bonus logs per rank',
            detailedDescription: 'You spot the best timber in every tree. Increases the quantity of logs harvested.',
            position: { x: 450, y: 430 },
            maxRank: 2,
            effects: [
                { type: 'lootBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['woodcutting_foundation'],
            tier: 2
        },

        // === TIER 3: Specialization ===
        'woodcutting_sustained': {
            id: 'woodcutting_sustained',
            name: 'Sustained Effort',
            description: '+2% speed per consecutive chop (10% max) per rank',
            detailedDescription: 'You find your rhythm when working the same tree. Each consecutive chop is faster, up to 10% bonus. Resets when you switch trees or stop.',
            position: { x: 150, y: 330 },
            maxRank: 2,
            effects: [
                { type: 'stackingSpeed', proc: 'sustainedEffort', value: 0.02, maxStacks: 0.10, perRank: true }
            ],
            prerequisites: ['woodcutting_swift'],
            tier: 3
        },
        'woodcutting_roots': {
            id: 'woodcutting_roots',
            name: 'Deep Roots',
            description: '4% seeds, 0.5% Ancient Seed per rank',
            detailedDescription: 'You learn to harvest seeds from felled trees. 4% chance per rank to find tree seeds. Rare 0.5% chance per rank to discover an Ancient Seed - a mysterious amalgamation that grows into an Eldritch Sapling when planted (Farming 50, 10hr, 20k XP). <span class="pet-synergy"><span class="husbandry-icon"></span> Your pets have learned to identify these rare seeds.</span>',
            position: { x: 300, y: 330 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'seedDrop', value: 0.04, perRank: true },
                { type: 'procChance', proc: 'ancientSeedDrop', value: 0.005, perRank: true }
            ],
            prerequisites: ['woodcutting_tools'],
            tier: 3
        },
        'woodcutting_sharp': {
            id: 'woodcutting_sharp',
            name: 'Sharp Edge',
            description: '5% chance for double logs per rank',
            detailedDescription: 'A well-maintained axe cleaves clean. Chance to harvest double logs from each swing.',
            position: { x: 450, y: 330 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'doubleLogs', value: 0.05, perRank: true }
            ],
            prerequisites: ['woodcutting_keen'],
            tier: 3
        },

        // === TIER 4: Advanced ===
        'woodcutting_extended': {
            id: 'woodcutting_extended',
            name: 'Extended Rhythm',
            description: 'Stacking speed cap increased to 20%',
            detailedDescription: 'Your endurance knows no bounds. The maximum stacking speed bonus from Sustained Effort is doubled to 20%.',
            position: { x: 150, y: 230 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'stackingCapBonus', value: 0.10, perRank: false }
            ],
            prerequisites: ['woodcutting_sustained'],
            tier: 4
        },
        'woodcutting_fertile': {
            id: 'woodcutting_fertile',
            name: 'Fertile Grove',
            description: 'Seeds → BloomHold, +10% Farming XP',
            detailedDescription: 'Your connection to the forest deepens. Tree seeds from woodcutting auto-deposit to BloomHold when unlocked. Gain +10% Farming XP when planting tree seeds.',
            position: { x: 300, y: 230 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'seedsToBloomHold', value: 1, perRank: false },
                { type: 'flatBonus', bonus: 'farmingXPBonus', value: 0.10, perRank: false }
            ],
            prerequisites: ['woodcutting_roots'],
            tier: 4
        },
        'woodcutting_prime': {
            id: 'woodcutting_prime',
            name: 'Prime Timber',
            description: '+25% log value for Woodworking',
            detailedDescription: 'You know quality wood when you see it. Logs count as 25% more when used in Woodworking recipes (1 log = 1.25 logs).',
            position: { x: 450, y: 230 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'primeTimber', value: 0.25, perRank: false }
            ],
            prerequisites: ['woodcutting_sharp'],
            tier: 4
        },

        // === TIER 5: Capstones (LEFT/RIGHT) + Timber (CENTER) ===
        'woodcutting_rhythmic': {
            id: 'woodcutting_rhythmic',
            name: 'Rhythmic Precision',
            description: '+8% all proc chances at max stack',
            detailedDescription: 'When fully in rhythm, everything clicks. While at maximum stacking speed bonus, all woodcutting proc chances (double logs, seeds, ash, timber) are increased by 8%.',
            position: { x: 150, y: 130 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'rhythmicPrecision', value: 0.08, perRank: false }
            ],
            prerequisites: ['woodcutting_extended'],
            tier: 5
        },
        'woodcutting_timber': {
            id: 'woodcutting_timber',
            name: 'Timber!',
            description: '3% chance for 5x logs',
            detailedDescription: 'Sometimes the whole tree comes down at once. Rare chance to harvest a massive haul of 5x logs.',
            position: { x: 300, y: 130 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'timberProc', value: 0.03, perRank: false }
            ],
            prerequisites: ['woodcutting_fertile'],
            tier: 5
        },
        'woodcutting_kindling': {
            id: 'woodcutting_kindling',
            name: 'Kindling Master',
            description: 'Guaranteed ash (1/8 low, 1/5 high tier)',
            detailedDescription: 'You master the art of finding kindling. Guaranteed 1 ash every 8 chops on low-tier trees (Common Timber, Ironwood, Softbark, Goldleaf) or every 5 chops on high-tier trees (Redgrain, Gravewood, Arcanewood). Useful for Alchemy and Glyphbinding. <span class="pet-synergy"><span class="husbandry-icon"></span> Your pets bring back extra ashes from their expeditions.</span>',
            position: { x: 450, y: 130 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'kindlingMaster', value: 1, perRank: false }
            ],
            prerequisites: ['woodcutting_prime'],
            tier: 5
        },

        // === TIER 6: CENTER Super-Capstone ===
        'woodcutting_blessing': {
            id: 'woodcutting_blessing',
            name: "Nature's Blessing",
            description: 'Timber! also drops 1-3 random seeds',
            detailedDescription: 'The forest rewards your dedication. When Timber! procs, you also receive 1-3 random tree seeds alongside the massive log haul.',
            position: { x: 300, y: 50 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'naturesBlessing', value: 1, perRank: false }
            ],
            prerequisites: ['woodcutting_timber'],
            tier: 6
        }
    },

    connections: [
        // From foundation
        { from: 'woodcutting_foundation', to: 'woodcutting_swift', style: 'straight' },
        { from: 'woodcutting_foundation', to: 'woodcutting_tools', style: 'straight' },
        { from: 'woodcutting_foundation', to: 'woodcutting_keen', style: 'straight' },

        // Tier 2 to Tier 3
        { from: 'woodcutting_swift', to: 'woodcutting_sustained', style: 'straight' },
        { from: 'woodcutting_tools', to: 'woodcutting_roots', style: 'straight' },
        { from: 'woodcutting_keen', to: 'woodcutting_sharp', style: 'straight' },

        // Tier 3 to Tier 4
        { from: 'woodcutting_sustained', to: 'woodcutting_extended', style: 'straight' },
        { from: 'woodcutting_roots', to: 'woodcutting_fertile', style: 'straight' },
        { from: 'woodcutting_sharp', to: 'woodcutting_prime', style: 'straight' },

        // Tier 4 to Tier 5
        { from: 'woodcutting_extended', to: 'woodcutting_rhythmic', style: 'straight' },
        { from: 'woodcutting_fertile', to: 'woodcutting_timber', style: 'straight' },
        { from: 'woodcutting_prime', to: 'woodcutting_kindling', style: 'straight' },

        // Tier 5 to Tier 6 (CENTER only)
        { from: 'woodcutting_timber', to: 'woodcutting_blessing', style: 'straight' }
    ]
};

// ============================================
// FISHING TALENT TREE - "Patience & Surprise"
// ============================================

export const FISHING_TREE = {
    id: SKILLS.FISHING,
    name: 'Fishing',
    shape: 'hook',
    canvasSize: { width: 600, height: 500 },
    backgroundColor: '#1a1a2e',
    accentColor: '#4682b4', // Ocean blue

    totalRanks: 20,

    nodes: {
        // === TIER 1: Foundation ===
        'fishing_foundation': {
            id: 'fishing_foundation',
            name: "Fisher's Foundation",
            description: '+5% Fishing XP per rank',
            detailedDescription: 'Learn the patience required for successful fishing. Each rank increases experience gained from all fishing activities.',
            position: { x: 300, y: 450 },
            maxRank: 3,
            effects: [
                { type: 'xpBonus', value: 0.05, perRank: true }
            ],
            prerequisites: [],
            tier: 1
        },

        // === TIER 2: Three Paths Start ===
        // LEFT - Patient Angler (speed/efficiency)
        'fishing_swift': {
            id: 'fishing_swift',
            name: 'Quick Cast',
            description: '-5% fishing time per rank',
            detailedDescription: 'Your casting and reeling becomes more efficient. Reduces the time to catch any fish.',
            position: { x: 150, y: 350 },
            maxRank: 2,
            effects: [
                { type: 'speedBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['fishing_foundation'],
            tier: 2
        },
        // CENTER - Master Angler (tools/cooking synergy)
        'fishing_tools': {
            id: 'fishing_tools',
            name: 'Rod Mastery',
            description: '+10% rod effectiveness per rank',
            detailedDescription: 'You understand the flex and strength of your rod. Amplifies the speed bonus from your equipped fishing rod.',
            position: { x: 300, y: 350 },
            maxRank: 2,
            effects: [
                { type: 'toolBonus', value: 0.10, perRank: true }
            ],
            prerequisites: ['fishing_foundation'],
            tier: 2
        },
        // RIGHT - Fortune Seeker (loot/doubles)
        'fishing_keen': {
            id: 'fishing_keen',
            name: 'Lucky Line',
            description: '+5% bonus fish per rank',
            detailedDescription: 'Fortune favors the patient angler. Increases the quantity of fish caught.',
            position: { x: 450, y: 350 },
            maxRank: 2,
            effects: [
                { type: 'lootBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['fishing_foundation'],
            tier: 2
        },

        // === TIER 3: Path Development ===
        // LEFT - Stacking speed bonus
        'fishing_rhythm': {
            id: 'fishing_rhythm',
            name: "Angler's Rhythm",
            description: '+3% speed per consecutive catch (15% max)',
            detailedDescription: 'You get into a rhythm when fishing the same spot. Each consecutive catch is faster, up to 15% bonus. Resets when you switch spots or stop.',
            position: { x: 150, y: 250 },
            maxRank: 1,
            effects: [
                { type: 'stackingSpeed', proc: 'anglersRhythm', value: 0.03, maxStacks: 0.15, perRank: false }
            ],
            prerequisites: ['fishing_swift'],
            tier: 3
        },
        // CENTER - Bonus items
        'fishing_bycatch': {
            id: 'fishing_bycatch',
            name: 'Bycatch',
            description: '4% chance for bonus items per rank',
            detailedDescription: 'Sometimes you catch more than fish. Chance to find seeds, herbs, or bones in the water while fishing. <span class="pet-synergy"><span class="husbandry-icon"></span> Your pets find variety items on Combat contracts.</span>',
            position: { x: 300, y: 250 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'bycatch', value: 0.04, perRank: true }
            ],
            prerequisites: ['fishing_tools'],
            tier: 3
        },
        // RIGHT - Double fish
        'fishing_hooked': {
            id: 'fishing_hooked',
            name: 'Well Hooked',
            description: '5% chance for double fish per rank',
            detailedDescription: 'Your hook rarely misses its mark. Chance to catch two fish instead of one.',
            position: { x: 450, y: 250 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'doubleFish', value: 0.05, perRank: true }
            ],
            prerequisites: ['fishing_keen'],
            tier: 3
        },

        // === TIER 4: Path Advancement ===
        // LEFT - Extend stacking cap
        'fishing_extended': {
            id: 'fishing_extended',
            name: 'Extended Rhythm',
            description: 'Stacking speed cap increased to 25%',
            detailedDescription: 'Your patience knows no bounds. The maximum stacking speed bonus from Angler\'s Rhythm is increased from 15% to 25%.',
            position: { x: 150, y: 150 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'stackingCapBonus', value: 0.10, perRank: false }
            ],
            prerequisites: ['fishing_rhythm'],
            tier: 4
        },
        // CENTER - Auto-cook chance
        'fishing_fresh': {
            id: 'fishing_fresh',
            name: 'Fresh Preparation',
            description: '10% chance fish is already cooked',
            detailedDescription: 'Your expertise lets you prepare fish on the spot. Chance to receive cooked fish instead of raw, granting both Fishing XP and Cooking XP.',
            position: { x: 300, y: 150 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'autoCook', value: 0.10, perRank: false }
            ],
            prerequisites: ['fishing_bycatch'],
            tier: 4
        },
        // RIGHT - Double ALL drops
        'fishing_haul': {
            id: 'fishing_haul',
            name: 'Lucky Haul',
            description: '8% chance to double ALL drops',
            detailedDescription: 'When fortune smiles, everything doubles. Chance to double both fish and any bycatch items on the same catch.',
            position: { x: 450, y: 150 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'luckyHaul', value: 0.08, perRank: false }
            ],
            prerequisites: ['fishing_hooked'],
            tier: 4
        },

        // === TIER 5: Capstones ===
        // LEFT - Instant catch
        'fishing_perfect': {
            id: 'fishing_perfect',
            name: 'Perfect Cast',
            description: '5% chance to instantly complete next catch',
            detailedDescription: 'Sometimes your cast is so perfect, the fish practically jumps onto your hook. The next catch after a proc completes instantly.',
            position: { x: 150, y: 50 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'instantCatch', value: 0.05, perRank: false }
            ],
            prerequisites: ['fishing_extended'],
            tier: 5
        },
        // CENTER - Higher auto-cook with Cooking XP
        'fishing_flame': {
            id: 'fishing_flame',
            name: 'Flame-Kissed Catch',
            description: '15% chance fish is already cooked',
            detailedDescription: 'Your mastery of preparation is legendary. Higher chance to receive cooked fish, granting both Fishing XP and Cooking XP.',
            position: { x: 300, y: 50 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'autoCook', value: 0.15, perRank: false }
            ],
            prerequisites: ['fishing_fresh'],
            tier: 5
        },
        // RIGHT - Chain doubles
        'fishing_frenzy': {
            id: 'fishing_frenzy',
            name: 'Feeding Frenzy',
            description: 'After any double proc, next catch is guaranteed double',
            detailedDescription: 'Success breeds success. After any double fish proc (Well Hooked or Lucky Haul), your next catch is guaranteed to be doubled.',
            position: { x: 450, y: 50 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'feedingFrenzy', value: 1.0, perRank: false }
            ],
            prerequisites: ['fishing_haul'],
            tier: 5
        }
    },

    connections: [
        // Tier 1 to Tier 2 (foundation to three paths)
        { from: 'fishing_foundation', to: 'fishing_swift', style: 'straight' },
        { from: 'fishing_foundation', to: 'fishing_tools', style: 'straight' },
        { from: 'fishing_foundation', to: 'fishing_keen', style: 'straight' },

        // Tier 2 to Tier 3 (each path continues)
        { from: 'fishing_swift', to: 'fishing_rhythm', style: 'straight' },
        { from: 'fishing_tools', to: 'fishing_bycatch', style: 'straight' },
        { from: 'fishing_keen', to: 'fishing_hooked', style: 'straight' },

        // Tier 3 to Tier 4 (paths continue)
        { from: 'fishing_rhythm', to: 'fishing_extended', style: 'straight' },
        { from: 'fishing_bycatch', to: 'fishing_fresh', style: 'straight' },
        { from: 'fishing_hooked', to: 'fishing_haul', style: 'straight' },

        // Tier 4 to Tier 5 (capstones)
        { from: 'fishing_extended', to: 'fishing_perfect', style: 'straight' },
        { from: 'fishing_fresh', to: 'fishing_flame', style: 'straight' },
        { from: 'fishing_haul', to: 'fishing_frenzy', style: 'straight' }
    ]
};

// ============================================
// COOKING TALENT TREE - "Nourishment Mastery"
// ============================================

export const COOKING_TREE = {
    id: SKILLS.COOKING,
    name: 'Cooking',
    shape: 'pot',
    canvasSize: { width: 600, height: 500 },
    backgroundColor: '#1a1a2e',
    accentColor: '#ff8c00', // Dark orange (cooking fire)

    totalRanks: 20,

    nodes: {
        // === TIER 1: Foundation ===
        'cooking_foundation': {
            id: 'cooking_foundation',
            name: "Chef's Foundation",
            description: '+5% Cooking XP per rank',
            detailedDescription: 'Master the basics of the kitchen. Each rank increases experience gained from all cooking activities.',
            position: { x: 300, y: 450 },
            maxRank: 3,
            effects: [
                { type: 'xpBonus', value: 0.05, perRank: true }
            ],
            prerequisites: [],
            tier: 1
        },

        // === TIER 2: Three Paths Start ===
        // LEFT - Speed Chef (efficiency)
        'cooking_swift': {
            id: 'cooking_swift',
            name: 'Quick Flame',
            description: '-5% cooking time per rank',
            detailedDescription: 'You learn to work the heat more efficiently. Reduces the time to prepare any dish.',
            position: { x: 150, y: 350 },
            maxRank: 2,
            effects: [
                { type: 'speedBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['cooking_foundation'],
            tier: 2
        },
        // CENTER - Master Nutritionist (healing/combat)
        'cooking_tools': {
            id: 'cooking_tools',
            name: 'Stove Mastery',
            description: '+10% stove effectiveness per rank',
            detailedDescription: 'You understand the nuances of your cooking range. Amplifies the speed bonus from your equipped stove.',
            position: { x: 300, y: 350 },
            maxRank: 2,
            effects: [
                { type: 'toolBonus', value: 0.10, perRank: true }
            ],
            prerequisites: ['cooking_foundation'],
            tier: 2
        },
        // RIGHT - Feast Master (production)
        'cooking_hearty': {
            id: 'cooking_hearty',
            name: 'Hearty Portions',
            description: '+5% bonus food per rank',
            detailedDescription: 'Your portions are generous but efficient. Increases the quantity of food prepared.',
            position: { x: 450, y: 350 },
            maxRank: 2,
            effects: [
                { type: 'lootBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['cooking_foundation'],
            tier: 2
        },

        // === TIER 3: Path Development ===
        // LEFT - Ingredient save
        'cooking_efficient': {
            id: 'cooking_efficient',
            name: 'Efficient Cooking',
            description: '5% chance to save ingredients per rank',
            detailedDescription: 'Waste not, want not in the kitchen. Chance to cook without consuming ingredients.',
            position: { x: 150, y: 250 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'ingredientSave', value: 0.05, perRank: true }
            ],
            prerequisites: ['cooking_swift'],
            tier: 3
        },
        // CENTER - Food healing bonus
        'cooking_nourishing': {
            id: 'cooking_nourishing',
            name: 'Nourishing Meals',
            description: '+3 HP to all cooked food',
            detailedDescription: 'Your cooking is more nutritious. All food you cook heals additional HP when consumed.',
            position: { x: 300, y: 250 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'foodHealBonus', value: 3, perRank: false }
            ],
            prerequisites: ['cooking_tools'],
            tier: 3
        },
        // RIGHT - Double food
        'cooking_seared': {
            id: 'cooking_seared',
            name: 'Seared Perfection',
            description: '5% chance for double food per rank',
            detailedDescription: 'Your technique produces extra portions. Chance to create two dishes from one cooking action.',
            position: { x: 450, y: 250 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'doubleFood', value: 0.05, perRank: true }
            ],
            prerequisites: ['cooking_hearty'],
            tier: 3
        },

        // === TIER 4: Path Advancement ===
        // LEFT - Stacking speed
        'cooking_flow': {
            id: 'cooking_flow',
            name: 'Kitchen Flow',
            description: '+3% speed per consecutive cook (15% max)',
            detailedDescription: 'You get into a rhythm in the kitchen. Each consecutive cook is faster, up to 15% bonus. Resets when you switch recipes or stop.',
            position: { x: 150, y: 150 },
            maxRank: 1,
            effects: [
                { type: 'stackingSpeed', proc: 'kitchenFlow', value: 0.03, maxStacks: 0.15, perRank: false }
            ],
            prerequisites: ['cooking_efficient'],
            tier: 4
        },
        // CENTER - Combat food save
        'cooking_helping': {
            id: 'cooking_helping',
            name: 'Second Helping',
            description: '10% chance food not consumed when eating',
            detailedDescription: 'Your food is so satisfying that sometimes a bite is enough. When eating in combat, chance to not consume the food.',
            position: { x: 300, y: 150 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'secondHelping', value: 0.10, perRank: false }
            ],
            prerequisites: ['cooking_nourishing'],
            tier: 4
        },
        // RIGHT - Fishing synergy
        'cooking_synergy': {
            id: 'cooking_synergy',
            name: 'Flame-Kissed Mastery',
            description: 'Fishing auto-cook can trigger double food',
            detailedDescription: 'Your cooking expertise extends to the riverbank. When fish are auto-cooked via Fishing talents, your Seared Perfection double food chance can apply.',
            position: { x: 450, y: 150 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'flameKissedMastery', value: 1, perRank: false }
            ],
            prerequisites: ['cooking_seared'],
            tier: 4
        },

        // === TIER 5: Capstones ===
        // LEFT - Prep chain
        'cooking_mise': {
            id: 'cooking_mise',
            name: 'Mise en Place',
            description: 'Ingredient save → next 3 cooks 25% faster',
            detailedDescription: 'Preparation is everything. After an ingredient save proc, your next 3 cooking actions are 25% faster.',
            position: { x: 150, y: 50 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'miseEnPlace', value: 1, perRank: false }
            ],
            prerequisites: ['cooking_flow'],
            tier: 5
        },
        // CENTER - HoT duration
        'cooking_comfort': {
            id: 'cooking_comfort',
            name: 'Comfort Food',
            description: '+25% HoT duration on empowered food (Online only)',
            detailedDescription: '<b>Online only</b> — does not apply during offline progress.<br/><br/>Your empowered dishes sustain longer. The heal over time effect on tier 7-9 empowered food lasts 25% longer.',
            position: { x: 300, y: 50 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'hotDurationBonus', value: 0.25, perRank: false }
            ],
            prerequisites: ['cooking_helping'],
            tier: 5
        },
        // RIGHT - 5x food
        'cooking_banquet': {
            id: 'cooking_banquet',
            name: 'Banquet',
            description: '4% chance for 5x food',
            detailedDescription: 'Sometimes the stars align and everything comes together perfectly. Rare chance to produce five times the normal output.',
            position: { x: 450, y: 50 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'banquet', value: 0.04, perRank: false }
            ],
            prerequisites: ['cooking_synergy'],
            tier: 5
        }
    },

    connections: [
        // Tier 1 to Tier 2 (foundation to three paths)
        { from: 'cooking_foundation', to: 'cooking_swift', style: 'straight' },
        { from: 'cooking_foundation', to: 'cooking_tools', style: 'straight' },
        { from: 'cooking_foundation', to: 'cooking_hearty', style: 'straight' },

        // Tier 2 to Tier 3 (each path continues)
        { from: 'cooking_swift', to: 'cooking_efficient', style: 'straight' },
        { from: 'cooking_tools', to: 'cooking_nourishing', style: 'straight' },
        { from: 'cooking_hearty', to: 'cooking_seared', style: 'straight' },

        // Tier 3 to Tier 4 (paths continue)
        { from: 'cooking_efficient', to: 'cooking_flow', style: 'straight' },
        { from: 'cooking_nourishing', to: 'cooking_helping', style: 'straight' },
        { from: 'cooking_seared', to: 'cooking_synergy', style: 'straight' },

        // Tier 4 to Tier 5 (capstones)
        { from: 'cooking_flow', to: 'cooking_mise', style: 'straight' },
        { from: 'cooking_helping', to: 'cooking_comfort', style: 'straight' },
        { from: 'cooking_synergy', to: 'cooking_banquet', style: 'straight' }
    ]
};

// ============================================
// CRAFTING TALENT TREE - "Artisan's Workshop"
// ============================================

/**
 * Crafting Talent Tree (Reworked)
 * Shape: Workshop - foundation at bottom, three crafting stations rising
 * 13 nodes, 20 total ranks
 *
 * Branches:
 * - LEFT (The Tailor): Speed and material efficiency for armor
 * - CENTER (The Merchant): Economy, gold savings, and patron rewards
 * - RIGHT (The Perfectionist): Output quality and XP mastery
 *
 * Layout:
 *     [MasterTailor]   [GrandArtisan]   [ArtisansPride]
 *           \              |                /
 *     [BulkProducer]  [PatronsFavor]  [InspiredCreation]
 *           \              |                /
 *     [MaterialEff]   [OnTheHouse]    [MasterCrafter]
 *           \              |                /
 *     [SwiftStitch]  [SteadyHands]   [QualityFocus]
 *           \              |                /
 *            \----[ArtisansFoundation]----/
 */
export const CRAFTING_TREE = {
    id: SKILLS.CRAFTING,
    name: 'Crafting',
    shape: 'workshop',
    canvasSize: { width: 600, height: 550 },
    backgroundColor: '#1a1a2e',
    accentColor: '#9370db', // Medium purple (artisan cloth)

    totalRanks: 20,

    nodes: {
        // === TIER 1: Foundation ===
        'crafting_foundation': {
            id: 'crafting_foundation',
            name: "Artisan's Foundation",
            description: '+7.5% Crafting XP per rank',
            detailedDescription: 'Study the fundamentals of craftsmanship. Each rank increases experience gained from all crafting activities.',
            position: { x: 300, y: 480 },
            maxRank: 2,
            effects: [
                { type: 'xpBonus', value: 0.075, perRank: true }
            ],
            prerequisites: [],
            tier: 1
        },

        // === TIER 2: Three Paths ===
        'crafting_swift': {
            id: 'crafting_swift',
            name: 'Swift Stitch',
            description: '+5% crafting speed per rank',
            detailedDescription: 'Your needle flies through fabric with practiced precision. Reduces the time to complete any crafting action.',
            position: { x: 150, y: 380 },
            maxRank: 2,
            effects: [
                { type: 'speedBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['crafting_foundation'],
            tier: 2
        },
        'crafting_steady': {
            id: 'crafting_steady',
            name: 'Steady Hands',
            description: '+10% tool effectiveness per rank',
            detailedDescription: 'Your hands never waver. Amplifies the speed bonus from your equipped crafting tools.',
            position: { x: 300, y: 380 },
            maxRank: 2,
            effects: [
                { type: 'toolBonus', value: 0.10, perRank: true }
            ],
            prerequisites: ['crafting_foundation'],
            tier: 2
        },
        'crafting_quality': {
            id: 'crafting_quality',
            name: 'Quality Focus',
            description: '+5% bonus output per rank',
            detailedDescription: 'You never settle for "good enough." Increases the quantity of items crafted through meticulous attention to detail.',
            position: { x: 450, y: 380 },
            maxRank: 2,
            effects: [
                { type: 'lootBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['crafting_foundation'],
            tier: 2
        },

        // === TIER 3: Specialization ===
        'crafting_efficient': {
            id: 'crafting_efficient',
            name: 'Material Efficiency',
            description: '15% chance to save all materials',
            detailedDescription: 'You waste nothing in your work. Chance to craft without consuming any materials.',
            position: { x: 150, y: 280 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'materialSave', value: 0.15, perRank: false }
            ],
            prerequisites: ['crafting_swift'],
            tier: 3
        },
        'crafting_onthehouse': {
            id: 'crafting_onthehouse',
            name: 'On the House',
            description: '7.5% chance to waive gold cost per rank',
            detailedDescription: 'Sometimes the pieces just fall into place perfectly. Chance to craft without spending gold.',
            position: { x: 300, y: 280 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'goldWaive', value: 0.075, perRank: true }
            ],
            prerequisites: ['crafting_steady'],
            tier: 3
        },
        'crafting_master': {
            id: 'crafting_master',
            name: 'Master Crafter',
            description: '5% chance for double output per rank',
            detailedDescription: 'Your expertise produces extra results. Chance to create two items from one crafting action.',
            position: { x: 450, y: 280 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'doubleOutput', value: 0.05, perRank: true }
            ],
            prerequisites: ['crafting_quality'],
            tier: 3
        },

        // === TIER 4: Advanced ===
        'crafting_bulk': {
            id: 'crafting_bulk',
            name: 'Bulk Producer',
            description: '10% chance for 2 extra items',
            detailedDescription: 'When you hit your rhythm, you really hit it. Chance to produce two additional items on top of normal output.',
            position: { x: 150, y: 180 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'bulkProduce', value: 0.10, perRank: false }
            ],
            prerequisites: ['crafting_efficient'],
            tier: 4
        },
        'crafting_patron': {
            id: 'crafting_patron',
            name: "Patron's Favor",
            description: '5% chance per rank for tip gold',
            detailedDescription: 'Word of your skill has spread. Chance to receive a gold tip equal to 25% of the crafted item\'s value.',
            position: { x: 300, y: 180 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'patronsFavor', value: 0.05, perRank: true }
            ],
            prerequisites: ['crafting_onthehouse'],
            tier: 4
        },
        'crafting_inspired': {
            id: 'crafting_inspired',
            name: 'Inspired Creation',
            description: '10% chance for double XP',
            detailedDescription: 'Sometimes inspiration strikes and your work transcends the ordinary. Chance for a craft to award double experience.',
            position: { x: 450, y: 180 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'inspiredCraft', value: 0.10, perRank: false }
            ],
            prerequisites: ['crafting_master'],
            tier: 4
        },

        // === TIER 5: Capstones ===
        'crafting_tailor': {
            id: 'crafting_tailor',
            name: 'Master Tailor',
            description: 'Armor crafting 20% faster',
            detailedDescription: 'You have mastered the art of protective garments. All leather and magic armor crafting time is reduced by 20%.',
            position: { x: 150, y: 80 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'armorCraftSpeed', value: 0.20, perRank: false }
            ],
            prerequisites: ['crafting_bulk'],
            tier: 5
        },
        'crafting_grand': {
            id: 'crafting_grand',
            name: 'Grand Artisan',
            description: 'All crafting bonuses +10%',
            detailedDescription: 'True mastery of the artisan\'s craft. All your crafting talent bonuses are amplified by 10%.',
            position: { x: 300, y: 80 },
            maxRank: 1,
            effects: [
                { type: 'globalBonus', value: 0.10, perRank: false }
            ],
            prerequisites: ['crafting_patron'],
            tier: 5
        },
        'crafting_pride': {
            id: 'crafting_pride',
            name: "Artisan's Pride",
            description: '+25% XP for highest-tier crafts',
            detailedDescription: 'You take pride in pushing your limits. Gain 25% bonus XP when crafting items at your current highest unlocked tier.',
            position: { x: 450, y: 80 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'highTierXPBonus', value: 0.25, perRank: false }
            ],
            prerequisites: ['crafting_inspired'],
            tier: 5
        }
    },

    connections: [
        // From foundation to Tier 2
        { from: 'crafting_foundation', to: 'crafting_swift', style: 'straight' },
        { from: 'crafting_foundation', to: 'crafting_steady', style: 'straight' },
        { from: 'crafting_foundation', to: 'crafting_quality', style: 'straight' },

        // Tier 2 to Tier 3
        { from: 'crafting_swift', to: 'crafting_efficient', style: 'straight' },
        { from: 'crafting_steady', to: 'crafting_onthehouse', style: 'straight' },
        { from: 'crafting_quality', to: 'crafting_master', style: 'straight' },

        // Tier 3 to Tier 4
        { from: 'crafting_efficient', to: 'crafting_bulk', style: 'straight' },
        { from: 'crafting_onthehouse', to: 'crafting_patron', style: 'straight' },
        { from: 'crafting_master', to: 'crafting_inspired', style: 'straight' },

        // Tier 4 to Tier 5 (capstones)
        { from: 'crafting_bulk', to: 'crafting_tailor', style: 'straight' },
        { from: 'crafting_patron', to: 'crafting_grand', style: 'straight' },
        { from: 'crafting_inspired', to: 'crafting_pride', style: 'straight' }
    ]
};

// ============================================
// PYRE TALENT TREE - "Sacred Flames"
// ============================================

/**
 * Pyre Talent Tree (Reworked)
 * Shape: Sacred flame - foundation at bottom, three flames rising
 * 13 nodes, 20 total ranks
 *
 * Branches:
 * - LEFT (Flame Master): Speed and burning efficiency
 * - CENTER (Ash Collector): Tool mastery and ash production
 * - RIGHT (Veil Seeker): Ritual power and spiritual rewards
 *
 * Layout:
 *     [Inferno]      [CinderStorm]    [Veil's Blessing]
 *          \              |              /
 *   [BlazingRhythm]  [AshMastery]  [RitualPotency]
 *            \            |            /
 *     [EfficientBurn] [SmokeRead] [SpiritualOffer]
 *            \            |            /
 *      [SacredKindling] [Brazier] [SpiritualFocus]
 *               \         |         /
 *                    [Kindling]
 */
export const PYRE_TREE = {
    id: SKILLS.PYRE,
    name: 'Pyre',
    shape: 'flame',
    canvasSize: { width: 700, height: 500 },
    backgroundColor: '#1a1a2e',
    accentColor: '#ff6b35', // Sacred flame orange

    totalRanks: 20,

    nodes: {
        // ==================== TIER 1 - FOUNDATION ====================
        'pyre_foundation': {
            id: 'pyre_foundation',
            name: 'Kindling',
            description: '+5% Pyre XP per rank',
            detailedDescription: 'Learn the sacred art of tending flames. Each rank increases experience gained from all pyre activities.',
            position: { x: 350, y: 430 },
            maxRank: 3,
            effects: [
                { type: 'xpBonus', value: 0.05, perRank: true }
            ],
            prerequisites: [],
            tier: 1
        },

        // ==================== TIER 2 - THREE PATHS BEGIN ====================
        // LEFT: Flame Master - Speed and efficiency
        'pyre_speed': {
            id: 'pyre_speed',
            name: 'Sacred Kindling',
            description: '-5% burn time per rank',
            detailedDescription: 'Your fires catch quickly with practiced technique. Reduces the time to complete burning actions.',
            position: { x: 180, y: 340 },
            maxRank: 2,
            effects: [
                { type: 'speedBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['pyre_foundation'],
            tier: 2
        },
        // CENTER: Ash Collector - Tool mastery and output
        'pyre_brazier': {
            id: 'pyre_brazier',
            name: 'Brazier Mastery',
            description: '+10% brazier effectiveness per rank',
            detailedDescription: 'You understand how to get the most from your brazier. Amplifies the speed bonus from your equipped brazier.',
            position: { x: 350, y: 340 },
            maxRank: 2,
            effects: [
                { type: 'toolBonus', value: 0.10, perRank: true }
            ],
            prerequisites: ['pyre_foundation'],
            tier: 2
        },
        // RIGHT: Veil Seeker - Rituals and spiritual power
        'pyre_focus': {
            id: 'pyre_focus',
            name: 'Spiritual Focus',
            description: '+5% ritual XP per rank',
            detailedDescription: 'Your connection to the Veil strengthens. Increases experience gained from ritual burning.',
            position: { x: 520, y: 340 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'ritualXpBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['pyre_foundation'],
            tier: 2
        },

        // ==================== TIER 3 - DEVELOPMENT ====================
        'pyre_efficient': {
            id: 'pyre_efficient',
            name: 'Efficient Burn',
            description: '5% chance to save logs per rank',
            detailedDescription: 'Your flames burn so efficiently that sometimes logs are preserved. Chance to not consume the log when burning.',
            position: { x: 180, y: 250 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'logSave', value: 0.05, perRank: true }
            ],
            prerequisites: ['pyre_speed'],
            tier: 3
        },
        'pyre_smoke': {
            id: 'pyre_smoke',
            name: 'Smoke Reading',
            description: '5% bonus ashes per rank',
            detailedDescription: 'You read the smoke and coax more ash from each burn. Chance for bonus ashes when burning. <span class="pet-synergy"><span class="husbandry-icon"></span> All your pets find extra ashes.</span>',
            position: { x: 350, y: 250 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'bonusAshes', value: 0.05, perRank: true }
            ],
            prerequisites: ['pyre_brazier'],
            tier: 3
        },
        'pyre_spiritual': {
            id: 'pyre_spiritual',
            name: 'Spiritual Offering',
            description: '10% chance to save bones in rituals',
            detailedDescription: 'The spirits accept your offerings graciously. Chance to preserve bones when performing rituals.',
            position: { x: 520, y: 250 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'boneSave', value: 0.10, perRank: false }
            ],
            prerequisites: ['pyre_focus'],
            tier: 3
        },

        // ==================== TIER 4 - PRE-CAPSTONES ====================
        'pyre_rhythm': {
            id: 'pyre_rhythm',
            name: 'Blazing Rhythm',
            description: '+2% speed per consecutive burn (15% max)',
            detailedDescription: 'You find your rhythm in the flames. Each consecutive burn of the same log type is faster, up to 15% bonus. Resets when you switch logs or stop.',
            position: { x: 180, y: 160 },
            maxRank: 1,
            effects: [
                { type: 'stackingSpeed', value: 0.02, maxStacks: 0.15, perRank: false }
            ],
            prerequisites: ['pyre_efficient'],
            tier: 4
        },
        'pyre_ashmaster': {
            id: 'pyre_ashmaster',
            name: 'Ash Mastery',
            description: '+10% ash output',
            detailedDescription: 'Master the transformation of wood to ash. Increases the quantity of ashes produced from all burning.',
            position: { x: 350, y: 160 },
            maxRank: 1,
            effects: [
                { type: 'lootBonus', value: 0.10, perRank: false }
            ],
            prerequisites: ['pyre_smoke'],
            tier: 4
        },
        'pyre_potency': {
            id: 'pyre_potency',
            name: 'Ritual Potency',
            description: '+20% ritual XP multiplier',
            detailedDescription: 'Your communion with the Veil grows stronger. Significantly enhanced XP from ritual burning.',
            position: { x: 520, y: 160 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'ritualXpBonus', value: 0.20, perRank: false }
            ],
            prerequisites: ['pyre_spiritual'],
            tier: 4
        },

        // ==================== TIER 5 - CAPSTONES ====================
        'pyre_inferno': {
            id: 'pyre_inferno',
            name: 'Inferno',
            description: '5% chance for instant next burn',
            detailedDescription: 'Your flames burn with such intensity that they sometimes ignite the next log instantly. After completing a burn, chance for your next burn to complete immediately.',
            position: { x: 180, y: 70 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'instantBurn', value: 0.05, perRank: false }
            ],
            prerequisites: ['pyre_rhythm'],
            tier: 5
        },
        'pyre_cinder': {
            id: 'pyre_cinder',
            name: 'Cinder Storm',
            description: '3% chance for 3x ash',
            detailedDescription: 'Sometimes the flames produce an extraordinary amount of ash. Rare chance to receive triple ashes from a single burn.',
            position: { x: 350, y: 70 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'cinderStorm', value: 0.03, perRank: false }
            ],
            prerequisites: ['pyre_ashmaster'],
            tier: 5
        },
        'pyre_blessing': {
            id: 'pyre_blessing',
            name: "Veil's Blessing",
            description: '15% ritual chance for random potion',
            detailedDescription: 'The spirits reward your devotion with alchemical gifts. When performing rituals, chance to receive a random potion based on your Alchemy level. <span class="pet-synergy"><span class="husbandry-icon"></span> Your pets may bring back random potions.</span>',
            position: { x: 520, y: 70 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'veilBlessing', value: 0.15, perRank: false }
            ],
            prerequisites: ['pyre_potency'],
            tier: 5
        }
    },

    connections: [
        // Tier 1 to Tier 2
        { from: 'pyre_foundation', to: 'pyre_speed', style: 'straight' },
        { from: 'pyre_foundation', to: 'pyre_brazier', style: 'straight' },
        { from: 'pyre_foundation', to: 'pyre_focus', style: 'straight' },

        // Tier 2 to Tier 3
        { from: 'pyre_speed', to: 'pyre_efficient', style: 'straight' },
        { from: 'pyre_brazier', to: 'pyre_smoke', style: 'straight' },
        { from: 'pyre_focus', to: 'pyre_spiritual', style: 'straight' },

        // Tier 3 to Tier 4
        { from: 'pyre_efficient', to: 'pyre_rhythm', style: 'straight' },
        { from: 'pyre_smoke', to: 'pyre_ashmaster', style: 'straight' },
        { from: 'pyre_spiritual', to: 'pyre_potency', style: 'straight' },

        // Tier 4 to Tier 5 (Capstones)
        { from: 'pyre_rhythm', to: 'pyre_inferno', style: 'straight' },
        { from: 'pyre_ashmaster', to: 'pyre_cinder', style: 'straight' },
        { from: 'pyre_potency', to: 'pyre_blessing', style: 'straight' }
    ]
};

/**
 * Alchemy Talent Tree
 * Shape: Potion flask - wide at bottom, narrow neck at top
 * 10 nodes, 17 total ranks
 *
 * Layout:
 *              [Bubbling Brews]
 *                /          \
 *     [Double Batch]    [Essence Overflow]
 *           |                  |
 *   [Potent Extraction]  [Bloom Touched]
 *           |                  |
 *    [Careful Hands]    [Rapid Infusion]
 *           \                /
 *   [Swift Preparation] [Brewing Wisdom]
 *              \          /
 *           [Alchemist's Foundation]
 */
export const ALCHEMY_TREE = {
    id: SKILLS.ALCHEMY,
    name: 'Alchemy',
    shape: 'potion',
    canvasSize: { width: 600, height: 500 },
    backgroundColor: '#1a1a2e',
    accentColor: '#7b68ee', // Mystic purple

    totalRanks: 20,

    nodes: {
        // === TIER 1: Foundation ===
        'alchemy_foundation': {
            id: 'alchemy_foundation',
            name: "Alchemist's Foundation",
            description: '+5% Alchemy XP per rank',
            detailedDescription: 'Master the fundamentals of herb preparation and potion brewing. Each rank increases experience gained from all alchemy activities.',
            position: { x: 300, y: 450 },
            maxRank: 3,
            effects: [
                { type: 'xpBonus', value: 0.05, perRank: true }
            ],
            prerequisites: [],
            tier: 1
        },

        // === TIER 2: Three Paths Start ===
        // LEFT - Herb Master (cleaning efficiency)
        'alchemy_swift': {
            id: 'alchemy_swift',
            name: 'Swift Preparation',
            description: '-5% herb cleaning time per rank',
            detailedDescription: 'Your hands move with practiced efficiency when cleaning herbs. Reduces the time to clean grimy herbs.',
            position: { x: 150, y: 350 },
            maxRank: 2,
            effects: [
                { type: 'speedBonus', subType: 'cleaning', value: 0.05, perRank: true }
            ],
            prerequisites: ['alchemy_foundation'],
            tier: 2
        },
        // CENTER - Master Brewer (production)
        'alchemy_rapid': {
            id: 'alchemy_rapid',
            name: 'Rapid Infusion',
            description: '-5% potion brewing time per rank',
            detailedDescription: 'You have mastered the art of quick infusion. Reduces the time to brew potions.',
            position: { x: 300, y: 350 },
            maxRank: 2,
            effects: [
                { type: 'speedBonus', subType: 'brewing', value: 0.05, perRank: true }
            ],
            prerequisites: ['alchemy_foundation'],
            tier: 2
        },
        // RIGHT - Elixir Savant (consumption/effects)
        'alchemy_overflow': {
            id: 'alchemy_overflow',
            name: 'Essence Overflow',
            description: '+10% potion duration per rank',
            detailedDescription: 'Your potions are infused with extra essence. All potions you drink last longer.',
            position: { x: 450, y: 350 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'potionDuration', value: 0.10, perRank: true }
            ],
            prerequisites: ['alchemy_foundation'],
            tier: 2
        },

        // === TIER 3: Path Development ===
        // LEFT - Herb save
        'alchemy_careful': {
            id: 'alchemy_careful',
            name: 'Careful Hands',
            description: '5% chance to save grimy herbs per rank',
            detailedDescription: 'Your careful technique sometimes preserves the herb during cleaning. Chance to not consume the grimy herb.',
            position: { x: 150, y: 250 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'herbSave', value: 0.05, perRank: true }
            ],
            prerequisites: ['alchemy_swift'],
            tier: 3
        },
        // CENTER - Herb conservation when brewing
        'alchemy_conserve': {
            id: 'alchemy_conserve',
            name: 'Herb Conservation',
            description: '5% chance to save herbs when brewing per rank',
            detailedDescription: 'Your efficient brewing technique sometimes preserves the ingredients. Chance to not consume herbs when brewing potions.',
            position: { x: 300, y: 250 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'brewHerbSave', value: 0.05, perRank: true }
            ],
            prerequisites: ['alchemy_rapid'],
            tier: 3
        },
        // RIGHT - BloomHold auto-deposit
        'alchemy_bloom': {
            id: 'alchemy_bloom',
            name: 'Bloom Touched',
            description: 'Herbs auto-deposit to BloomHold',
            detailedDescription: 'Your connection to the BloomHold allows herbs to flow directly into storage. Herbs gathered from Combat loot or Gatherer zones automatically deposit to BloomHold if enabled.',
            position: { x: 450, y: 250 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'bloomAutoDeposit', value: 1, perRank: false }
            ],
            prerequisites: ['alchemy_overflow'],
            tier: 3
        },

        // === TIER 4: Path Advancement ===
        // LEFT - Bonus herb from cleaning (transmutation flavor)
        'alchemy_transmute': {
            id: 'alchemy_transmute',
            name: 'Transmutation',
            description: '5% chance to find bonus higher-tier herb',
            detailedDescription: 'Sometimes when cleaning grimy herbs, you discover another herb stuck to it - and it\'s of higher quality! Chance to receive a bonus herb of the next tier when cleaning. If already cleaning the highest tier herb, you find an extra of the same herb instead. <span class="pet-synergy"><span class="husbandry-icon"></span> Your pets\' herb drops may be upgraded.</span>',
            position: { x: 150, y: 150 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'transmutation', value: 0.05, perRank: false }
            ],
            prerequisites: ['alchemy_careful'],
            tier: 4
        },
        // CENTER - Double potions
        'alchemy_double': {
            id: 'alchemy_double',
            name: 'Double Batch',
            description: '10% chance for 2 potions',
            detailedDescription: 'Your brewing expertise sometimes yields a bonus potion. Chance to produce two potions instead of one.',
            position: { x: 300, y: 150 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'doubleBrew', value: 0.10, perRank: false }
            ],
            prerequisites: ['alchemy_conserve'],
            tier: 4
        },
        // RIGHT - Potion not consumed
        'alchemy_saver': {
            id: 'alchemy_saver',
            name: 'Potion Saver',
            description: '10% chance potion not consumed when drinking (Online only)',
            detailedDescription: '<b>Online only</b> — does not apply during offline progress.<br/><br/>Your potions are so potent that sometimes a sip is all you need. Chance to not consume the potion when drinking in combat.',
            position: { x: 450, y: 150 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'potionSaver', value: 0.10, perRank: false }
            ],
            prerequisites: ['alchemy_bloom'],
            tier: 4
        },

        // === TIER 5: Capstones ===
        // LEFT - 5x herbs
        'alchemy_botanical': {
            id: 'alchemy_botanical',
            name: 'Botanical Genius',
            description: '4% chance for 5x herbs when cleaning',
            detailedDescription: 'Your mastery of herb preparation occasionally yields extraordinary results. Rare chance to produce five times the normal output when cleaning herbs.',
            position: { x: 150, y: 50 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'fiveXHerbs', value: 0.04, perRank: false }
            ],
            prerequisites: ['alchemy_transmute'],
            tier: 5
        },
        // CENTER - Bubbling Brews (combat potion enhancement)
        'alchemy_bubbling': {
            id: 'alchemy_bubbling',
            name: 'Bubbling Brews',
            description: '5% chance for +30% potion effect in combat',
            detailedDescription: 'Your potions bubble with unstable power. When you attack or take damage, there\'s a chance your active potion effects are amplified by 30% for a short time. Has a 10 second internal cooldown. (Online only)',
            position: { x: 300, y: 50 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'bubblingBrew', value: 0.05, perRank: false }
            ],
            prerequisites: ['alchemy_double'],
            tier: 5
        },
        // RIGHT - Lingering Essence (combat kills extend potions)
        'alchemy_lingering': {
            id: 'alchemy_lingering',
            name: 'Lingering Essence',
            description: 'Combat kills can extend potion duration (Online only)',
            detailedDescription: '<b>Online only</b> — does not apply during offline progress.<br/><br/>The essence of fallen foes sustains your elixirs. Combat kills have a 15% chance to extend one random active potion by 5 seconds. Each potion can be extended by a maximum of 30 seconds total.',
            position: { x: 450, y: 50 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'lingeringEssence', value: 0.15, perRank: false }
            ],
            prerequisites: ['alchemy_saver'],
            tier: 5
        }
    },

    connections: [
        // Tier 1 to Tier 2 (foundation to three paths)
        { from: 'alchemy_foundation', to: 'alchemy_swift', style: 'straight' },
        { from: 'alchemy_foundation', to: 'alchemy_rapid', style: 'straight' },
        { from: 'alchemy_foundation', to: 'alchemy_overflow', style: 'straight' },

        // Tier 2 to Tier 3 (each path continues)
        { from: 'alchemy_swift', to: 'alchemy_careful', style: 'straight' },
        { from: 'alchemy_rapid', to: 'alchemy_conserve', style: 'straight' },
        { from: 'alchemy_overflow', to: 'alchemy_bloom', style: 'straight' },

        // Tier 3 to Tier 4 (paths continue)
        { from: 'alchemy_careful', to: 'alchemy_transmute', style: 'straight' },
        { from: 'alchemy_conserve', to: 'alchemy_double', style: 'straight' },
        { from: 'alchemy_bloom', to: 'alchemy_saver', style: 'straight' },

        // Tier 4 to Tier 5 (capstones)
        { from: 'alchemy_transmute', to: 'alchemy_botanical', style: 'straight' },
        { from: 'alchemy_double', to: 'alchemy_bubbling', style: 'straight' },
        { from: 'alchemy_saver', to: 'alchemy_lingering', style: 'straight' }
    ]
};

/**
 * Gatherer Talent Tree
 * Shape: Backpack/satchel - wide at top, narrow at bottom
 * 10 nodes, 17 total ranks
 *
 * Layout:
 *              [Expedition Master]
 *                /          \
 *     [Large Deposit]    [Material Mastery]
 *           |                  |
 *     [Resourceful]      [Pet Companion]
 *           |                  |
 *  [Bountiful Harvest]    [Lucky Find]
 *           \                /
 *      [Swift Hands]    [Keen Senses]
 *              \          /
 *           [Forager's Foundation]
 */
/**
 * Gatherer Talent Tree (3-Branch Rework)
 * Shape: Satchel - foundation at bottom, three branches upward
 * Theme: Three paths to gathering mastery
 *
 * Branches:
 * - LEFT (Swift Forager): Speed, stacking zone bonus, yield at max stack
 * - CENTER (Naturalist): Cross-skill synergy with Farming, herb upgrades, BloomHold
 * - RIGHT (Expedition Scout): Rare finds, pet synergy, big hauls
 *
 * Total: 14 nodes, 22 ranks, 5 tiers
 */
export const GATHERER_TREE = {
    id: SKILLS.GATHERER,
    name: 'Gatherer',
    description: 'Enhance your foraging and gathering abilities',
    shape: 'satchel',
    canvasSize: { width: 600, height: 500 },
    backgroundColor: '#1a2e1a',
    accentColor: '#7cb342', // Forest green

    totalRanks: 22,

    nodes: {
        // === TIER 1: Foundation (Shared) ===
        'gatherer_foundation': {
            id: 'gatherer_foundation',
            name: "Forager's Foundation",
            description: '+5% XP per rank',
            detailedDescription: 'Your knowledge of the wilds deepens with each expedition. Increases all Gatherer experience gained.',
            position: { x: 300, y: 420 },
            maxRank: 3,
            effects: [
                { type: 'xpBonus', value: 0.05, perRank: true }
            ],
            prerequisites: [],
            tier: 1
        },

        // === TIER 2: Three Branches Start ===
        // LEFT: Swift Forager
        'gatherer_swift': {
            id: 'gatherer_swift',
            name: 'Swift Hands',
            description: '-5% gather time per rank',
            detailedDescription: 'Your hands move with practiced efficiency, quickly collecting resources from each gathering spot.',
            position: { x: 150, y: 340 },
            maxRank: 2,
            effects: [
                { type: 'speedBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['gatherer_foundation'],
            tier: 2
        },
        // CENTER: Naturalist
        'gatherer_keen': {
            id: 'gatherer_keen',
            name: 'Keen Senses',
            description: '+10% tool effectiveness per rank',
            detailedDescription: 'Your heightened awareness helps you find the best spots to gather, making your tools more effective.',
            position: { x: 300, y: 340 },
            maxRank: 2,
            effects: [
                { type: 'toolBonus', value: 0.10, perRank: true }
            ],
            prerequisites: ['gatherer_foundation'],
            tier: 2
        },
        // RIGHT: Expedition Scout
        'gatherer_bountiful': {
            id: 'gatherer_bountiful',
            name: 'Bountiful Harvest',
            description: '+10% bonus material chance per rank',
            detailedDescription: 'You have a knack for finding extra resources. Increases the chance to find bonus materials like herbs, crops, and cloth.',
            position: { x: 450, y: 340 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'bonusMaterialChance', value: 0.10, perRank: true }
            ],
            prerequisites: ['gatherer_foundation'],
            tier: 2
        },

        // === TIER 3: Specialization ===
        // LEFT: Zone Mastery (stacking speed)
        'gatherer_zone_mastery': {
            id: 'gatherer_zone_mastery',
            name: 'Zone Mastery',
            description: '+2% speed per consecutive gather (10% cap)',
            detailedDescription: 'Familiarity breeds efficiency. Each consecutive gather in the same zone increases your speed, up to 10%. Resets when changing zones.',
            position: { x: 150, y: 260 },
            maxRank: 2,
            effects: [
                { type: 'stackingSpeed', value: 0.02, maxStacks: 0.10, perRank: true }
            ],
            prerequisites: ['gatherer_swift'],
            tier: 3
        },
        // CENTER: Seed Finder
        'gatherer_seed_finder': {
            id: 'gatherer_seed_finder',
            name: 'Seed Finder',
            description: '3% chance for crop/herb seeds per rank',
            detailedDescription: 'Your trained eye spots seeds hidden among the foliage. Chance to find random crop or herb seeds while gathering. <span class="pet-synergy"><span class="husbandry-icon"></span> Your pets also find seeds on Resource contracts.</span>',
            position: { x: 300, y: 260 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'seedFind', value: 0.03, perRank: true }
            ],
            prerequisites: ['gatherer_keen'],
            tier: 3
        },
        // RIGHT: Lucky Find
        'gatherer_lucky': {
            id: 'gatherer_lucky',
            name: 'Lucky Find',
            description: '5% chance for double food per rank',
            detailedDescription: 'Sometimes you stumble upon an especially abundant patch. Chance to double your food drops from gathering.',
            position: { x: 450, y: 260 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'doubleFood', value: 0.05, perRank: true }
            ],
            prerequisites: ['gatherer_bountiful'],
            tier: 3
        },

        // === TIER 4: Advanced ===
        // LEFT: Extended Expedition
        'gatherer_extended': {
            id: 'gatherer_extended',
            name: 'Extended Expedition',
            description: 'Stacking speed cap increased to 20%',
            detailedDescription: 'Your endurance allows you to maintain focus longer. Zone Mastery stacking bonus cap increased from 10% to 20%.',
            position: { x: 150, y: 180 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'stackingCapBonus', value: 0.10, perRank: false }
            ],
            prerequisites: ['gatherer_zone_mastery'],
            tier: 4
        },
        // CENTER: Herb Attunement
        'gatherer_herb_attunement': {
            id: 'gatherer_herb_attunement',
            name: 'Herb Attunement',
            description: 'Bonus herbs drop one tier higher',
            detailedDescription: 'Your connection to nature reveals the finest specimens. All bonus herb drops from gathering are upgraded to the next tier.',
            position: { x: 300, y: 180 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'herbTierUpgrade', value: 1, perRank: false }
            ],
            prerequisites: ['gatherer_seed_finder'],
            tier: 4
        },
        // RIGHT: Pet Companion
        'gatherer_pet': {
            id: 'gatherer_pet',
            name: 'Pet Companion',
            description: '20% chance pet assists (2x drops + Husbandry XP)',
            detailedDescription: 'Your loyal companion occasionally helps with gathering. When they assist, all drops are doubled and you gain Husbandry experience. Works even if your pet is resting.',
            position: { x: 400, y: 180 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'petAssist', value: 0.20, perRank: false }
            ],
            prerequisites: ['gatherer_lucky'],
            tier: 4
        },
        // RIGHT: Awakening Specialist
        'gatherer_awakening': {
            id: 'gatherer_awakening',
            name: 'Awakening Specialist',
            description: '+35% awakening material drop chance (additive)',
            detailedDescription: 'Your expertise in finding rare materials is unmatched. Significantly increases the chance to find awakening materials (Twisted Root Fiber, Living Arcane Husk, etc.). <span class="pet-synergy"><span class="husbandry-icon"></span> Your pets bring back more awakening materials.</span>',
            position: { x: 500, y: 180 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'awakeningChanceBonus', value: 0.35, perRank: false }
            ],
            prerequisites: ['gatherer_lucky'],
            tier: 4
        },

        // === TIER 5: Capstones ===
        // LEFT: Gathering Momentum
        'gatherer_momentum': {
            id: 'gatherer_momentum',
            name: 'Gathering Momentum',
            description: 'At max stack: +25% all yields',
            detailedDescription: 'When your Zone Mastery stacking bonus is at maximum, all gathering yields are increased by 25%. Rewards consistent zone grinding.',
            position: { x: 150, y: 100 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'momentumYieldBonus', value: 0.25, perRank: false }
            ],
            prerequisites: ['gatherer_extended'],
            tier: 5
        },
        // CENTER: Nature's Harmony
        'gatherer_harmony': {
            id: 'gatherer_harmony',
            name: "Nature's Harmony",
            description: 'Seeds to BloomHold, 10% material transmutation',
            detailedDescription: 'Your bond with nature is complete. Seeds from Seed Finder auto-deposit to BloomHold. Additionally, 10% chance for bonus materials to upgrade to the next tier.',
            position: { x: 300, y: 100 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'seedsToBloomHold', value: 1, perRank: false },
                { type: 'procChance', proc: 'materialTransmutation', value: 0.10, perRank: false }
            ],
            prerequisites: ['gatherer_herb_attunement'],
            tier: 5
        },
        // RIGHT: Master Explorer
        'gatherer_explorer': {
            id: 'gatherer_explorer',
            name: 'Master Explorer',
            description: '5% Hidden Cache, 5% for 10x drops',
            detailedDescription: 'You are a legendary explorer. 5% chance to discover a Hidden Cache containing bonus mixed materials. 5% chance for 10x all drops from a gather.',
            position: { x: 450, y: 100 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'hiddenCache', value: 0.05, perRank: false },
                { type: 'procChance', proc: 'tenXDrops', value: 0.05, perRank: false }
            ],
            prerequisites: ['gatherer_pet', 'gatherer_awakening'],
            tier: 5
        }
    },

    connections: [
        // From foundation to all three branches
        { from: 'gatherer_foundation', to: 'gatherer_swift', style: 'straight' },
        { from: 'gatherer_foundation', to: 'gatherer_keen', style: 'straight' },
        { from: 'gatherer_foundation', to: 'gatherer_bountiful', style: 'straight' },

        // LEFT branch: Swift -> Zone Mastery -> Extended -> Momentum
        { from: 'gatherer_swift', to: 'gatherer_zone_mastery', style: 'straight' },
        { from: 'gatherer_zone_mastery', to: 'gatherer_extended', style: 'straight' },
        { from: 'gatherer_extended', to: 'gatherer_momentum', style: 'straight' },

        // CENTER branch: Keen -> Seed Finder -> Herb Attunement -> Harmony
        { from: 'gatherer_keen', to: 'gatherer_seed_finder', style: 'straight' },
        { from: 'gatherer_seed_finder', to: 'gatherer_herb_attunement', style: 'straight' },
        { from: 'gatherer_herb_attunement', to: 'gatherer_harmony', style: 'straight' },

        // RIGHT branch: Bountiful -> Lucky -> Pet/Awakening -> Explorer
        { from: 'gatherer_bountiful', to: 'gatherer_lucky', style: 'straight' },
        { from: 'gatherer_lucky', to: 'gatherer_pet', style: 'straight' },
        { from: 'gatherer_lucky', to: 'gatherer_awakening', style: 'straight' },
        { from: 'gatherer_pet', to: 'gatherer_explorer', style: 'straight' },
        { from: 'gatherer_awakening', to: 'gatherer_explorer', style: 'straight' }
    ]
};

/**
 * Pilfering Talent Tree
 * Shape: Dagger - handle at bottom, blade at top
 * Theme: Shadow Operations - stealth, speed, guild synergy
 *
 * Layout (V-shape with dual paths):
 *                [Phantom Strike]
 *                /              \
 *     [Master Thief]      [Quick Escape]
 *           |                    |
 *     [Shadow Clone]      [Combat Intel]
 *           |                    |
 *     [Light Touch]      [Guild Connections]
 *            \                  /
 *      [Quick Fingers]  [Gloves Mastery]
 *               \          /
 *            [Shadow's Foundation]
 */
export const PILFERING_TREE = {
    id: SKILLS.PILFERING,
    name: 'Pilfering',
    description: 'Master the art of shadow operations',
    shape: 'dagger',
    canvasSize: { width: 600, height: 520 },
    backgroundColor: '#1a1a2e',
    accentColor: '#2f4f4f', // Dark slate gray (shadow theme)

    // 3-branch design: The Fence (economy), The Shadow (core), The Syndicate (guild)
    // Total ranks: 3+2+2+1+1+2+1+1+2+2+1+1+1 = 20
    totalRanks: 20,

    nodes: {
        // === TIER 1: Foundation ===
        'pilfering_foundation': {
            id: 'pilfering_foundation',
            name: 'Sticky Fingers',
            description: '+5% XP per rank',
            detailedDescription: 'The shadows teach patience and precision. Increases all Pilfering experience gained.',
            position: { x: 300, y: 460 },
            maxRank: 3,
            effects: [
                { type: 'xpBonus', value: 0.05, perRank: true }
            ],
            prerequisites: [],
            tier: 1
        },

        // === TIER 2: Split ===
        'pilfering_swift': {
            id: 'pilfering_swift',
            name: 'Quick Hands',
            description: '-5% pickpocket time per rank',
            detailedDescription: 'Your fingers move with practiced speed, reducing the time needed to lift valuables.',
            position: { x: 150, y: 380 },
            maxRank: 2,
            effects: [
                { type: 'speedBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['pilfering_foundation'],
            tier: 2
        },
        'pilfering_tools': {
            id: 'pilfering_tools',
            name: 'Light Touch',
            description: '+10% gloves effectiveness per rank',
            detailedDescription: 'Your delicate touch maximizes your glove potential. Amplifies the speed bonus from equipped gloves.',
            position: { x: 450, y: 380 },
            maxRank: 2,
            effects: [
                { type: 'toolBonus', value: 0.10, perRank: true }
            ],
            prerequisites: ['pilfering_foundation'],
            tier: 2
        },

        // === TIER 3: Branch Split (3 paths) ===
        // LEFT - The Fence (Economy)
        'pilfering_coinmagnet': {
            id: 'pilfering_coinmagnet',
            name: 'Coin Magnet',
            description: '+10% coins from ALL sources',
            detailedDescription: 'Gold seems to find its way to you. Increases coin drops from combat, fishing, and all other sources.',
            position: { x: 100, y: 300 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'globalCoinBonus', value: 0.10, perRank: false }
            ],
            prerequisites: ['pilfering_swift'],
            tier: 3
        },
        // CENTER - The Shadow (Core Pilfering)
        'pilfering_clone': {
            id: 'pilfering_clone',
            name: 'Shadow Clone',
            description: '5% chance for 2x ALL (coins, XP, drops)',
            detailedDescription: 'Your shadow moves independently, doubling everything from a pickpocket - coins, experience, and any drops.',
            position: { x: 300, y: 300 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'shadowClone', value: 0.05, perRank: false }
            ],
            prerequisites: ['pilfering_swift', 'pilfering_tools'],
            tier: 3
        },
        // RIGHT - The Syndicate (Guild/Contracts)
        'pilfering_network': {
            id: 'pilfering_network',
            name: 'Network Contacts',
            description: '+10% gathering/processing assist per rank',
            detailedDescription: 'Your network extends beyond combat. Increases the chance pilferers assist on gathering and processing Bounty contracts.',
            position: { x: 500, y: 300 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'gatherProcessAssist', value: 0.10, perRank: true }
            ],
            prerequisites: ['pilfering_tools'],
            tier: 3
        },

        // === TIER 4: Path Advancement ===
        // LEFT - Fence Connections
        'pilfering_fence': {
            id: 'pilfering_fence',
            name: 'Fence Connections',
            description: '+15% shop sell prices',
            detailedDescription: 'You know the right people. All items sell for 15% more at shops thanks to your underworld contacts.',
            position: { x: 100, y: 220 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'shopSellBonus', value: 0.15, perRank: false }
            ],
            prerequisites: ['pilfering_coinmagnet'],
            tier: 4
        },
        // CENTER - Vanishing Act
        'pilfering_vanish': {
            id: 'pilfering_vanish',
            name: 'Vanishing Act',
            description: '5% chance to instantly complete pickpocket',
            detailedDescription: 'You move so fast, sometimes the mark doesn\'t even notice time passing. Chance to instantly complete a pickpocket action.',
            position: { x: 300, y: 220 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'vanishingAct', value: 0.05, perRank: false }
            ],
            prerequisites: ['pilfering_clone'],
            tier: 4
        },
        // RIGHT - Combat Intel
        'pilfering_intel': {
            id: 'pilfering_intel',
            name: 'Combat Intel',
            description: '+25% kill contract assist per rank',
            detailedDescription: 'Your pilferers gather combat intelligence. Increases the chance they assist on Bounty kill contracts.',
            position: { x: 500, y: 220 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'killContractAssist', value: 0.25, perRank: true }
            ],
            prerequisites: ['pilfering_network'],
            tier: 4
        },

        // === TIER 5: Pre-Capstone ===
        // RIGHT - Guild Efficiency (only right path has T5)
        'pilfering_guild': {
            id: 'pilfering_guild',
            name: 'Guild Efficiency',
            description: '+15% pilferer domain speed per rank',
            detailedDescription: 'Your reputation opens doors. Guild pilferers work faster in their assigned domains.',
            position: { x: 500, y: 140 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'pilfererSpeed', value: 0.15, perRank: true }
            ],
            prerequisites: ['pilfering_intel'],
            tier: 5
        },

        // === TIER 6: Capstones ===
        // LEFT - Golden Opportunity
        'pilfering_golden': {
            id: 'pilfering_golden',
            name: 'Golden Opportunity',
            description: '10% bonus gold when claiming chests',
            detailedDescription: 'Your eye for value is unmatched. When claiming guild chest, husbandry chest, or combat loot, 10% chance to find bonus gold equal to 25% of items\' sell value. Works with Architecting 99 auto-claims.',
            position: { x: 100, y: 60 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'goldenOpportunity', value: 0.10, perRank: false }
            ],
            prerequisites: ['pilfering_fence'],
            tier: 6
        },
        // CENTER - Master Thief (enhanced with 2x coins from removed Phantom Strike)
        'pilfering_master': {
            id: 'pilfering_master',
            name: 'Master Thief',
            description: '3% for 5x coins, 5% for 2x coins, 20% bonus loot',
            detailedDescription: 'The ultimate thief. 3% chance for 5x coins and 5% chance for 2x coins when pickpocketing, plus 20% chance to find bonus loot when killing mobs or pickpocketing.',
            position: { x: 300, y: 60 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'tenXCoins', value: 0.03, perRank: false },
                { type: 'procChance', proc: 'phantomStrike', value: 0.05, perRank: false },
                { type: 'procChance', proc: 'bonusLootFind', value: 0.20, perRank: false }
            ],
            prerequisites: ['pilfering_vanish'],
            tier: 6
        },
        // RIGHT - Underworld Kingpin
        'pilfering_kingpin': {
            id: 'pilfering_kingpin',
            name: 'Underworld Kingpin',
            description: 'Contract assists reduce 2x normal amount',
            detailedDescription: 'You run the largest thieves\' network in the realm. When pilferers assist on contracts, they reduce requirements by double the normal amount.',
            position: { x: 500, y: 60 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'contractAssistMultiplier', value: 2.0, perRank: false }
            ],
            prerequisites: ['pilfering_guild'],
            tier: 6
        }
    },

    connections: [
        // From foundation to split
        { from: 'pilfering_foundation', to: 'pilfering_swift', style: 'straight' },
        { from: 'pilfering_foundation', to: 'pilfering_tools', style: 'straight' },

        // Tier 2 to Tier 3 (split into 3 branches)
        { from: 'pilfering_swift', to: 'pilfering_coinmagnet', style: 'straight' },
        { from: 'pilfering_swift', to: 'pilfering_clone', style: 'straight' },
        { from: 'pilfering_tools', to: 'pilfering_clone', style: 'straight' },
        { from: 'pilfering_tools', to: 'pilfering_network', style: 'straight' },

        // Tier 3 to Tier 4
        { from: 'pilfering_coinmagnet', to: 'pilfering_fence', style: 'straight' },
        { from: 'pilfering_clone', to: 'pilfering_vanish', style: 'straight' },
        { from: 'pilfering_network', to: 'pilfering_intel', style: 'straight' },

        // Tier 4 to Tier 5/Capstones
        { from: 'pilfering_fence', to: 'pilfering_golden', style: 'straight' },
        { from: 'pilfering_vanish', to: 'pilfering_master', style: 'straight' },
        { from: 'pilfering_intel', to: 'pilfering_guild', style: 'straight' },

        // Tier 5 to Capstone (right branch)
        { from: 'pilfering_guild', to: 'pilfering_kingpin', style: 'straight' }
    ]
};

/**
 * Glyphbinding Talent Tree (Reworked)
 * Shape: Arcane Workshop - foundation at bottom, three mystical paths rising
 * 13 nodes, 20 total ranks
 *
 * Branches:
 * - LEFT (The Inscriber): Ichor harvesting speed and efficiency
 * - CENTER (The Enchanter): Glyph crafting, ashes synergy, tools
 * - RIGHT (The Sage): Output, knowledge, and arcane power
 *
 * Layout:
 *     [IchorBurst]      [ArcaneConduit]     [SigilOfPlenty]
 *           \                |                  /
 *     [EfficientBindings] [ArcaneEfficiency] [EssenceOverflow]
 *           \                |                  /
 *     [EssenceSiphon]   [AshesAffinity]    [ResonantGlyphs]
 *           \                |                  /
 *     [SwiftInscriptions] [ChiselPrecision] [ArcaneFocus]
 *           \                |                  /
 *            \----[ArcaneAttunement]----------/
 */
export const GLYPHBINDING_TREE = {
    id: SKILLS.GLYPHBINDING,
    name: 'Glyphbinding',
    description: 'Master the arcane arts of ichor and glyph',
    shape: 'workshop',
    canvasSize: { width: 600, height: 550 },
    backgroundColor: '#1a1a2e',
    accentColor: '#9932cc', // Dark orchid (arcane theme)

    totalRanks: 20,

    nodes: {
        // === TIER 1: Foundation ===
        'glyphbinding_foundation': {
            id: 'glyphbinding_foundation',
            name: 'Arcane Attunement',
            description: '+7.5% XP per rank',
            detailedDescription: 'Your understanding of arcane principles deepens. Increases all Glyphbinding experience gained.',
            position: { x: 300, y: 480 },
            maxRank: 2,
            effects: [
                { type: 'xpBonus', value: 0.075, perRank: true }
            ],
            prerequisites: [],
            tier: 1
        },

        // === TIER 2: Three Paths ===
        'glyphbinding_swift': {
            id: 'glyphbinding_swift',
            name: 'Swift Inscriptions',
            description: '+5% speed per rank',
            detailedDescription: 'Your hands trace arcane patterns with practiced ease, reducing time for all glyphbinding actions.',
            position: { x: 150, y: 380 },
            maxRank: 2,
            effects: [
                { type: 'speedBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['glyphbinding_foundation'],
            tier: 2
        },
        'glyphbinding_tools': {
            id: 'glyphbinding_tools',
            name: 'Chisel Precision',
            description: '+10% chisel effectiveness per rank',
            detailedDescription: 'Master the art of chisel work. Amplifies the speed bonus from your equipped chisel.',
            position: { x: 300, y: 380 },
            maxRank: 2,
            effects: [
                { type: 'toolBonus', value: 0.10, perRank: true }
            ],
            prerequisites: ['glyphbinding_foundation'],
            tier: 2
        },
        'glyphbinding_focus': {
            id: 'glyphbinding_focus',
            name: 'Arcane Focus',
            description: '+5% loot bonus per rank',
            detailedDescription: 'Your focused mind extracts more from every action. Increases all glyphbinding outputs.',
            position: { x: 450, y: 380 },
            maxRank: 2,
            effects: [
                { type: 'lootBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['glyphbinding_foundation'],
            tier: 2
        },

        // === TIER 3: Specialization ===
        'glyphbinding_siphon': {
            id: 'glyphbinding_siphon',
            name: 'Essence Siphon',
            description: '+10% bonus ichor per rank',
            detailedDescription: 'Your attunement to arcane energies allows you to extract more essence. Increased chance for bonus ichor drops. <span class="pet-synergy"><span class="husbandry-icon"></span> Your pets find more ichor on Purification contracts.</span>',
            position: { x: 150, y: 280 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'bonusIchor', value: 0.10, perRank: true }
            ],
            prerequisites: ['glyphbinding_swift'],
            tier: 3
        },
        'glyphbinding_ashes': {
            id: 'glyphbinding_ashes',
            name: 'Ashes Affinity',
            description: 'Ashes bonus proc 75% (up from 50%)',
            detailedDescription: 'Your connection to the Pyre strengthens. When ashes are consumed during glyph crafting, the bonus output chance increases to 75%.',
            position: { x: 300, y: 280 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'ashesSuccessRate', value: 0.25, perRank: false }
            ],
            prerequisites: ['glyphbinding_tools'],
            tier: 3
        },
        'glyphbinding_resonant': {
            id: 'glyphbinding_resonant',
            name: 'Resonant Glyphs',
            description: '5% chance for double glyph per rank',
            detailedDescription: 'Your inscriptions resonate with extra power, occasionally producing duplicate glyphs. <span class="pet-synergy"><span class="husbandry-icon"></span> Your pets can find glyphs on Purification contracts.</span>',
            position: { x: 450, y: 280 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'doubleGlyph', value: 0.05, perRank: true }
            ],
            prerequisites: ['glyphbinding_focus'],
            tier: 3
        },

        // === TIER 4: Advanced ===
        'glyphbinding_efficient': {
            id: 'glyphbinding_efficient',
            name: 'Efficient Bindings',
            description: '10% ichor save per rank',
            detailedDescription: 'Your precise technique occasionally preserves materials. Chance to craft glyphs without consuming ichor.',
            position: { x: 150, y: 180 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'ichorSave', value: 0.10, perRank: true }
            ],
            prerequisites: ['glyphbinding_siphon'],
            tier: 4
        },
        'glyphbinding_efficiency': {
            id: 'glyphbinding_efficiency',
            name: 'Arcane Efficiency',
            description: 'Glyph crafting 20% faster',
            detailedDescription: 'Your mastery of glyph patterns allows faster inscription. Reduces glyph crafting time by 20%.',
            position: { x: 300, y: 180 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'glyphCraftSpeed', value: 0.20, perRank: false }
            ],
            prerequisites: ['glyphbinding_ashes'],
            tier: 4
        },
        'glyphbinding_overflow': {
            id: 'glyphbinding_overflow',
            name: 'Essence Overflow',
            description: '8% chance for triple glyph',
            detailedDescription: 'Excess arcane energy amplifies your work, occasionally producing triple the normal glyph output.',
            position: { x: 450, y: 180 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'tripleGlyph', value: 0.08, perRank: false }
            ],
            prerequisites: ['glyphbinding_resonant'],
            tier: 4
        },

        // === TIER 5: Capstones ===
        'glyphbinding_burst': {
            id: 'glyphbinding_burst',
            name: 'Ichor Burst',
            description: '10% chance for 3x ichor',
            detailedDescription: 'Sometimes the essence node releases a burst of power. Chance to receive triple ichor when harvesting.',
            position: { x: 150, y: 80 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'ichorBurst', value: 0.10, perRank: false }
            ],
            prerequisites: ['glyphbinding_efficient'],
            tier: 5
        },
        'glyphbinding_conduit': {
            id: 'glyphbinding_conduit',
            name: 'Arcane Conduit',
            description: 'Corruption becomes Arcane Surge',
            detailedDescription: 'You channel corruption into pure arcane power. When corruption procs during Glyphbinding, it becomes "Arcane Surge" - 40% normal duration but 3x all stat bonuses (+120% speed, 4x loot, +150% XP). A burst of incredible power.',
            position: { x: 300, y: 80 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'arcaneSurge', value: 1.0, perRank: false }
            ],
            prerequisites: ['glyphbinding_efficiency'],
            tier: 5
        },
        'glyphbinding_plenty': {
            id: 'glyphbinding_plenty',
            name: 'Sigil of Plenty',
            description: 'All glyph outputs +25%',
            detailedDescription: 'Your mastery of arcane symbols unlocks hidden potential. All glyph crafting yields are increased by 25%.',
            position: { x: 450, y: 80 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'glyphOutputBonus', value: 0.25, perRank: false }
            ],
            prerequisites: ['glyphbinding_overflow'],
            tier: 5
        }
    },

    connections: [
        // From foundation to Tier 2
        { from: 'glyphbinding_foundation', to: 'glyphbinding_swift', style: 'straight' },
        { from: 'glyphbinding_foundation', to: 'glyphbinding_tools', style: 'straight' },
        { from: 'glyphbinding_foundation', to: 'glyphbinding_focus', style: 'straight' },

        // Tier 2 to Tier 3
        { from: 'glyphbinding_swift', to: 'glyphbinding_siphon', style: 'straight' },
        { from: 'glyphbinding_tools', to: 'glyphbinding_ashes', style: 'straight' },
        { from: 'glyphbinding_focus', to: 'glyphbinding_resonant', style: 'straight' },

        // Tier 3 to Tier 4
        { from: 'glyphbinding_siphon', to: 'glyphbinding_efficient', style: 'straight' },
        { from: 'glyphbinding_ashes', to: 'glyphbinding_efficiency', style: 'straight' },
        { from: 'glyphbinding_resonant', to: 'glyphbinding_overflow', style: 'straight' },

        // Tier 4 to Tier 5 (capstones)
        { from: 'glyphbinding_efficient', to: 'glyphbinding_burst', style: 'straight' },
        { from: 'glyphbinding_efficiency', to: 'glyphbinding_conduit', style: 'straight' },
        { from: 'glyphbinding_overflow', to: 'glyphbinding_plenty', style: 'straight' }
    ]
};

/**
 * Woodworking Talent Tree
 * Shape: Bow - curved arc with arrow through center
 * Theme: "The Fletcher's Art" - precision crafting, batch production
 *
 * Layout (Bow shape):
 *                    [Grandmaster]
 *                         |
 *          [Bow Artisan]     [Arcane Carving]
 *                  \           /
 *    [Assembly Line] [Wood Whisperer] [Precise Fletching]
 *                  \      |      /
 *      [Swift Carving] [Tool Mastery] [Bulk Production]
 *                  \      |      /
 *                   [Foundation]
 */
export const WOODWORKING_TREE = {
    id: SKILLS.WOODWORKING,
    name: 'Woodworking',
    shape: 'bow',
    canvasSize: { width: 600, height: 500 },
    backgroundColor: '#1a1a2e',
    accentColor: '#8B4513', // Saddle brown (wood)

    totalRanks: 20,

    nodes: {
        // === TIER 1: Foundation ===
        'woodworking_foundation': {
            id: 'woodworking_foundation',
            name: "Fletcher's Foundation",
            description: '+5% Woodworking XP per rank',
            detailedDescription: 'Master the fundamentals of the craft. Each rank increases experience gained from all woodworking activities.',
            position: { x: 300, y: 450 },
            maxRank: 3,
            effects: [
                { type: 'xpBonus', value: 0.05, perRank: true }
            ],
            prerequisites: [],
            tier: 1
        },

        // === TIER 2: Three Paths Start ===
        // LEFT - Arrow Fletcher path (speed/production)
        'woodworking_rapid': {
            id: 'woodworking_rapid',
            name: 'Rapid Carving',
            description: '-5% craft time per rank',
            detailedDescription: 'Your knife strokes become fluid and efficient. Reduces the time to complete any woodworking action.',
            position: { x: 150, y: 350 },
            maxRank: 2,
            effects: [
                { type: 'speedBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['woodworking_foundation'],
            tier: 2
        },
        // CENTER - Master Craftsman path (efficiency)
        'woodworking_tools': {
            id: 'woodworking_tools',
            name: 'Tool Mastery',
            description: '+10% knife effectiveness per rank',
            detailedDescription: 'You understand how to get the most from your carving knife. Amplifies the speed bonus from your equipped knife.',
            position: { x: 300, y: 350 },
            maxRank: 2,
            effects: [
                { type: 'toolBonus', value: 0.10, perRank: true }
            ],
            prerequisites: ['woodworking_foundation'],
            tier: 2
        },
        // RIGHT - Arcane Woodworker path (magic synergy)
        'woodworking_arcane': {
            id: 'woodworking_arcane',
            name: 'Arcane Sensitivity',
            description: '+10% staff XP per rank',
            detailedDescription: 'You develop an affinity for magical timber. Increased experience when crafting staffs.',
            position: { x: 450, y: 350 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'staffXpBonus', value: 0.10, perRank: true }
            ],
            prerequisites: ['woodworking_foundation'],
            tier: 2
        },

        // === TIER 3: Path Development ===
        // LEFT - Arrow production volume
        'woodworking_bulk': {
            id: 'woodworking_bulk',
            name: 'Bulk Production',
            description: '+15% shaft output per rank',
            detailedDescription: 'You waste less wood when carving shafts. Increases the number of arrow shafts produced per craft.',
            position: { x: 150, y: 250 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'shaftBonus', value: 0.15, perRank: true }
            ],
            prerequisites: ['woodworking_rapid'],
            tier: 3
        },
        // CENTER - Resource efficiency
        'woodworking_whisperer': {
            id: 'woodworking_whisperer',
            name: 'Wood Whisperer',
            description: '8% chance to save logs',
            detailedDescription: 'You extract more from each piece of timber. Chance to not consume log materials when crafting.',
            position: { x: 300, y: 250 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'logSave', value: 0.08, perRank: false }
            ],
            prerequisites: ['woodworking_tools'],
            tier: 3
        },
        // RIGHT - Glyphbinding synergy
        'woodworking_resonance': {
            id: 'woodworking_resonance',
            name: 'Glyph Resonance',
            description: '+12% Glyphbinding XP when crafting staffs',
            detailedDescription: 'Working with magical timber deepens your arcane understanding. Gain bonus Glyphbinding experience when crafting any staff.',
            position: { x: 450, y: 250 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'glyphbindingXpOnStaff', value: 0.12, perRank: false }
            ],
            prerequisites: ['woodworking_arcane'],
            tier: 3
        },

        // === TIER 4: Path Advancement ===
        // LEFT - Bonus arrows
        'woodworking_fletching': {
            id: 'woodworking_fletching',
            name: 'Precise Fletching',
            description: '8% chance for +5 bonus arrows per rank',
            detailedDescription: 'Your arrow assembly technique is flawless. When crafting arrows, chance to produce 5 extra arrows.',
            position: { x: 150, y: 150 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'bonusArrows', value: 0.08, perRank: true }
            ],
            prerequisites: ['woodworking_bulk'],
            tier: 4
        },
        // CENTER - Stacking speed rhythm
        'woodworking_assembly': {
            id: 'woodworking_assembly',
            name: 'Assembly Line',
            description: '+3% speed per consecutive craft (15% max)',
            detailedDescription: 'You get into a rhythm when crafting the same item repeatedly. Each consecutive craft is faster, up to 15% bonus. Resets when you switch items or stop.',
            position: { x: 300, y: 150 },
            maxRank: 1,
            effects: [
                { type: 'stackingSpeed', proc: 'assemblyLine', value: 0.03, maxStacks: 0.15, perRank: false }
            ],
            prerequisites: ['woodworking_whisperer'],
            tier: 4
        },
        // RIGHT - Bonus random ichor
        'woodworking_overflow': {
            id: 'woodworking_overflow',
            name: 'Arcane Overflow',
            description: '10% chance for random ichor when crafting staffs',
            detailedDescription: 'Magical energy spills over during staff crafting. Chance to receive a random ichor type as a byproduct.',
            position: { x: 450, y: 150 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'bonusIchor', value: 0.10, perRank: false }
            ],
            prerequisites: ['woodworking_resonance'],
            tier: 4
        },

        // === TIER 5: Capstones ===
        // LEFT - Arrow Storm (3x arrows)
        'woodworking_storm': {
            id: 'woodworking_storm',
            name: 'Arrow Storm',
            description: '10% chance for 3x arrow output',
            detailedDescription: 'When the craft flows perfectly, arrows seem to assemble themselves. Chance for triple arrow output when crafting arrows.',
            position: { x: 150, y: 50 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'arrowStorm', value: 0.10, perRank: false }
            ],
            prerequisites: ['woodworking_fletching'],
            tier: 5
        },
        // CENTER - Perfect Form (instant craft)
        'woodworking_perfect': {
            id: 'woodworking_perfect',
            name: 'Perfect Form',
            description: '5% chance to instantly complete next craft',
            detailedDescription: 'Your mastery is so complete that sometimes crafts finish in the blink of an eye. The next craft after a proc completes instantly.',
            position: { x: 300, y: 50 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'instantCraft', value: 0.05, perRank: false }
            ],
            prerequisites: ['woodworking_assembly'],
            tier: 5
        },
        // RIGHT - Veil-Touched Timber (random glyph based on Glyphbinding level)
        'woodworking_veil': {
            id: 'woodworking_veil',
            name: 'Veil-Touched Timber',
            description: '15% chance for random glyph when crafting staffs',
            detailedDescription: 'The magical timber you work with yields arcane residue. Chance to receive a random glyph based on your Glyphbinding level when crafting any staff.',
            position: { x: 450, y: 50 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'veilTimber', value: 0.15, perRank: false }
            ],
            prerequisites: ['woodworking_overflow'],
            tier: 5
        }
    },

    connections: [
        // Tier 1 to Tier 2 (foundation to three paths)
        { from: 'woodworking_foundation', to: 'woodworking_rapid', style: 'straight' },
        { from: 'woodworking_foundation', to: 'woodworking_tools', style: 'straight' },
        { from: 'woodworking_foundation', to: 'woodworking_arcane', style: 'straight' },

        // Tier 2 to Tier 3 (each path continues)
        { from: 'woodworking_rapid', to: 'woodworking_bulk', style: 'straight' },
        { from: 'woodworking_tools', to: 'woodworking_whisperer', style: 'straight' },
        { from: 'woodworking_arcane', to: 'woodworking_resonance', style: 'straight' },

        // Tier 3 to Tier 4 (paths continue)
        { from: 'woodworking_bulk', to: 'woodworking_fletching', style: 'straight' },
        { from: 'woodworking_whisperer', to: 'woodworking_assembly', style: 'straight' },
        { from: 'woodworking_resonance', to: 'woodworking_overflow', style: 'straight' },

        // Tier 4 to Tier 5 (capstones)
        { from: 'woodworking_fletching', to: 'woodworking_storm', style: 'straight' },
        { from: 'woodworking_assembly', to: 'woodworking_perfect', style: 'straight' },
        { from: 'woodworking_overflow', to: 'woodworking_veil', style: 'straight' }
    ]
};

/**
 * Husbandry Talent Tree - "The Beast Master's Bond"
 * Theme: Deepening bonds with pets for faster contracts and better rewards
 * Shape: Paw print
 * Total ranks: 18
 */
/**
 * Husbandry Talent Tree (Reworked)
 * Shape: Paw - foundation at bottom, three branches leading to unique capstones
 * Theme: "The Beast Master's Path"
 *
 * Branches:
 * - LEFT (Contract Master): Speed, efficiency, contract XP
 * - CENTER (Beast Keeper): Pet power, feeding, multi-pet synergy
 * - RIGHT (Treasure Hunter): Loot, rare drops, cross-skill bonuses
 *
 * Super Capstone: Combat Companion (requires 2 of 3 T5 capstones)
 *
 * Layout:
 *                       [Combat Companion]
 *                      /        |        \
 *        [Pack Instinct] [Eternal Bond] [Gift Giver]
 *              |               |              |
 *     [Rapid Deployment] [Feeding Overflow] [Pack Mentality]
 *              |               |           [Forager's Friend]
 *       [Eager Learner]    [Pack Bond]    [Treasure Nose]
 *              |               |              |
 *      [Swift Contracts] [Efficient Feeding] [Bountiful Haul]
 *                   \          |          /
 *                     [Keeper's Foundation]
 */
export const HUSBANDRY_TREE = {
    id: SKILLS.HUSBANDRY,
    name: 'Husbandry',
    shape: 'paw',
    canvasSize: { width: 700, height: 600 },
    backgroundColor: '#1a1a2e',
    accentColor: '#8B4513',
    totalRanks: 19,

    nodes: {
        // ==================== TIER 1 - FOUNDATION ====================
        'husbandry_foundation': {
            id: 'husbandry_foundation',
            name: "Keeper's Foundation",
            description: '+5% Husbandry XP per rank',
            detailedDescription: 'Your bond with your pets deepens, granting +5% experience from all Husbandry activities.',
            position: { x: 350, y: 520 },
            maxRank: 3,
            effects: [
                { type: 'xpBonus', value: 0.05, perRank: true }
            ],
            prerequisites: [],
            tier: 1
        },

        // ==================== TIER 2 - THREE PATHS BEGIN ====================
        // LEFT: Contract Master
        'husbandry_swift': {
            id: 'husbandry_swift',
            name: 'Swift Contracts',
            description: '-5% contract duration',
            detailedDescription: 'Your pets work more efficiently, completing contracts 5% faster.',
            position: { x: 150, y: 430 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'contractSpeed', value: 0.05, perRank: false }
            ],
            prerequisites: ['husbandry_foundation'],
            tier: 2
        },

        // CENTER: Beast Keeper
        'husbandry_feeding': {
            id: 'husbandry_feeding',
            name: 'Efficient Feeding',
            description: '+20% food effectiveness',
            detailedDescription: 'Your feeding technique improves. Each food item provides 20% more speed bonus when feeding pets.',
            position: { x: 350, y: 430 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'feedingEfficiency', value: 0.20, perRank: false }
            ],
            prerequisites: ['husbandry_foundation'],
            tier: 2
        },

        // RIGHT: Treasure Hunter
        'husbandry_haul': {
            id: 'husbandry_haul',
            name: 'Bountiful Haul',
            description: '+15% chest loot quantity',
            detailedDescription: 'Your pets bring back more items from their expeditions. Chest rewards increased by 15%.',
            position: { x: 550, y: 430 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'chestLootBonus', value: 0.15, perRank: false }
            ],
            prerequisites: ['husbandry_foundation'],
            tier: 2
        },

        // ==================== TIER 3 - SPECIALIZATION ====================
        // LEFT: Contract XP
        'husbandry_eager': {
            id: 'husbandry_eager',
            name: 'Eager Learner',
            description: '10% chance for double contract XP',
            detailedDescription: 'Your pets learn quickly. 10% chance to earn double XP when completing a contract.',
            position: { x: 150, y: 340 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'doubleContractXP', value: 0.10, perRank: false }
            ],
            prerequisites: ['husbandry_swift'],
            tier: 3
        },

        // CENTER: Multi-pet synergy
        'husbandry_pack_bond': {
            id: 'husbandry_pack_bond',
            name: 'Pack Bond',
            description: '+5% contract speed per additional active pet per rank (max 15%)',
            detailedDescription: 'Your pets work better together. When multiple pets are on contracts, each gains +5% speed per additional active pet.',
            position: { x: 350, y: 340 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'packBondSpeed', value: 0.05, perRank: true }
            ],
            prerequisites: ['husbandry_feeding'],
            tier: 3
        },

        // RIGHT: Global rare loot
        'husbandry_treasure': {
            id: 'husbandry_treasure',
            name: 'Treasure Nose',
            description: '+3% global rare loot chance per rank',
            detailedDescription: 'Your pets have trained your eye for treasure. ALL skills have +3% chance for rare drops (gems, seeds, rare materials).',
            position: { x: 550, y: 340 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'globalRareLoot', value: 0.03, perRank: true }
            ],
            prerequisites: ['husbandry_haul'],
            tier: 3
        },

        // ==================== TIER 4 - ADVANCED ====================
        // LEFT: Contract head start
        'husbandry_rapid': {
            id: 'husbandry_rapid',
            name: 'Rapid Deployment',
            description: '15% chance contract starts 25% complete',
            detailedDescription: 'Your pets are eager to begin. When starting a contract, 15% chance it begins at 25% progress.',
            position: { x: 150, y: 250 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'rapidDeployment', value: 0.15, perRank: false }
            ],
            prerequisites: ['husbandry_eager'],
            tier: 4
        },

        // CENTER: Feeding overflow bonus
        'husbandry_overflow': {
            id: 'husbandry_overflow',
            name: 'Feeding Overflow',
            description: 'Excess food = +1% speed per 100 overflow (max +10%)',
            detailedDescription: 'Food fed past the cap is not wasted. Every 100 overflow food grants a permanent +1% contract speed bonus to that pet (max +10%).',
            position: { x: 350, y: 250 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'feedingOverflow', value: 0.01, perRank: false }
            ],
            prerequisites: ['husbandry_pack_bond'],
            tier: 4
        },

        // RIGHT: Cross-skill bonuses
        'husbandry_pack': {
            id: 'husbandry_pack',
            name: 'Pack Mentality',
            description: '+10% Bounty contract XP',
            detailedDescription: 'Your combat pets share their hunting tactics. Gain 10% bonus XP from completing Bounty skill contracts.',
            position: { x: 490, y: 250 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'bountyContractXP', value: 0.10, perRank: false }
            ],
            prerequisites: ['husbandry_treasure'],
            tier: 4
        },
        'husbandry_forager': {
            id: 'husbandry_forager',
            name: "Forager's Friend",
            description: '+15% Gatherer bonus material chance',
            detailedDescription: 'Your resource pets teach you foraging secrets. Gatherer skill has +15% chance for bonus materials.',
            position: { x: 610, y: 250 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'gathererBonusMaterial', value: 0.15, perRank: false }
            ],
            prerequisites: ['husbandry_treasure'],
            tier: 4
        },

        // ==================== TIER 5 - CAPSTONES ====================
        // LEFT: Pack Instinct
        'husbandry_instinct': {
            id: 'husbandry_instinct',
            name: 'Pack Instinct',
            description: 'Contract completion gives other active pets +15% progress (up to 95%)',
            detailedDescription: 'Your pack is synchronized. When any pet completes a contract, all other active pets instantly gain 15% progress on their contracts. Cannot boost a contract past 95% completion.',
            position: { x: 150, y: 150 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'packInstinct', value: 0.15, perRank: false }
            ],
            prerequisites: ['husbandry_rapid'],
            tier: 5
        },

        // CENTER: Eternal Bond
        'husbandry_eternal': {
            id: 'husbandry_eternal',
            name: 'Eternal Bond',
            description: 'Fully awakened pets can run 2 contracts simultaneously',
            detailedDescription: 'Your bond with tier 3 awakened pets is so strong they can multitask. Tier 3 pets can run two contracts at once.',
            position: { x: 350, y: 150 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'dualContracts', value: 1, perRank: false }
            ],
            prerequisites: ['husbandry_overflow'],
            tier: 5
        },

        // RIGHT: Gift Giver
        'husbandry_gift': {
            id: 'husbandry_gift',
            name: 'Gift Giver',
            description: '10% chance for bonus item matching pet role on contract complete',
            detailedDescription: '<b>Gift Pools (70% common, 30% rare):</b><br/><b>Gatherer:</b> Seeds, grimy herbs, logs | Rare: Ancient seed, voidbloom, arcanewood<br/><b>Combat:</b> Bones, hides, raw fish | Rare: Dragon bone, void drake hide<br/><b>Purifier:</b> Ashes, ichor | Rare: Purified core, refined ichor, minor glyphs<br/><b>Hybrid:</b> Any pool | <b>Elite:</b> Awakening mats, core fragments, major glyphs',
            position: { x: 550, y: 150 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'giftGiver', value: 0.10, perRank: false }
            ],
            prerequisites: ['husbandry_pack', 'husbandry_forager'],
            tier: 5
        },

        // ==================== TIER 6 - SUPER CAPSTONE ====================
        'husbandry_companion': {
            id: 'husbandry_companion',
            name: 'Combat Companion',
            description: 'Random active pet assists in combat (Online only)',
            detailedDescription: '<b>Online only</b> — does not apply during offline progress.<br/><br/>A random pet on contract joins you in battle! 10% chance per attack for a pet to strike the enemy and grant a 30-second buff based on their type. Rootling (Magic, +Regen), Felltracker (Melee, +Attack Speed), Ichorebound (Ranged, +Defense), Chimera (Random), Eidolon (Pure damage, all buffs at 50%).',
            position: { x: 350, y: 50 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'combatCompanion', value: 0.10, perRank: false }
            ],
            // Requires 2 of 3 T5 capstones
            prerequisites: ['husbandry_instinct', 'husbandry_eternal', 'husbandry_gift'],
            requirePrerequisites: 2,
            tier: 6
        }
    },

    connections: [
        // Tier 1 to Tier 2
        { from: 'husbandry_foundation', to: 'husbandry_swift', style: 'straight' },
        { from: 'husbandry_foundation', to: 'husbandry_feeding', style: 'straight' },
        { from: 'husbandry_foundation', to: 'husbandry_haul', style: 'straight' },

        // Tier 2 to Tier 3
        { from: 'husbandry_swift', to: 'husbandry_eager', style: 'straight' },
        { from: 'husbandry_feeding', to: 'husbandry_pack_bond', style: 'straight' },
        { from: 'husbandry_haul', to: 'husbandry_treasure', style: 'straight' },

        // Tier 3 to Tier 4
        { from: 'husbandry_eager', to: 'husbandry_rapid', style: 'straight' },
        { from: 'husbandry_pack_bond', to: 'husbandry_overflow', style: 'straight' },
        { from: 'husbandry_treasure', to: 'husbandry_pack', style: 'straight' },
        { from: 'husbandry_treasure', to: 'husbandry_forager', style: 'straight' },

        // Tier 4 to Tier 5
        { from: 'husbandry_rapid', to: 'husbandry_instinct', style: 'straight' },
        { from: 'husbandry_overflow', to: 'husbandry_eternal', style: 'straight' },
        { from: 'husbandry_pack', to: 'husbandry_gift', style: 'straight' },
        { from: 'husbandry_forager', to: 'husbandry_gift', style: 'straight' },

        // Tier 5 to Super Capstone (all connect, but only 2 required)
        { from: 'husbandry_instinct', to: 'husbandry_companion', style: 'straight' },
        { from: 'husbandry_eternal', to: 'husbandry_companion', style: 'straight' },
        { from: 'husbandry_gift', to: 'husbandry_companion', style: 'straight' }
    ]
};

// ========================================
// BOUNTY TALENT TREE (3-Branch Design)
// ========================================
export const BOUNTY_TREE = {
    id: SKILLS.BOUNTY,
    name: 'Bounty',
    shape: 'scroll',
    canvasSize: { width: 700, height: 600 },
    backgroundColor: '#1a1a2e',
    accentColor: '#DAA520', // Gold color for bounty theme

    totalRanks: 21,

    nodes: {
        // ==================== TIER 1 - FOUNDATION ====================
        'bounty_foundation': {
            id: 'bounty_foundation',
            name: "Hunter's Foundation",
            description: '+5% Bounty XP per rank',
            detailedDescription: 'Your reputation as a bounty hunter grows. Each rank increases experience gained from completing contracts.',
            position: { x: 350, y: 530 },
            maxRank: 3,
            effects: [
                { type: 'xpBonus', value: 0.05, perRank: true }
            ],
            prerequisites: [],
            tier: 1
        },

        // ==================== TIER 2 - THREE BRANCHES ====================
        // LEFT: Wealth Branch
        'bounty_lucrative': {
            id: 'bounty_lucrative',
            name: 'Lucrative Deals',
            description: '+10% gold rewards per rank',
            detailedDescription: 'You negotiate better terms for your contracts. Increases gold rewards from all completed contracts.',
            position: { x: 150, y: 430 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'contractGoldBonus', value: 0.10, perRank: true }
            ],
            prerequisites: ['bounty_foundation'],
            tier: 2
        },

        // CENTER: Efficiency Branch
        'bounty_efficient': {
            id: 'bounty_efficient',
            name: 'Efficient Tracker',
            description: '-5% contract requirements per rank',
            detailedDescription: 'Your efficiency impresses the contract givers. They accept slightly less to consider contracts complete.',
            position: { x: 350, y: 430 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'contractRequirementReduction', value: 0.05, perRank: true }
            ],
            prerequisites: ['bounty_foundation'],
            tier: 2
        },

        // RIGHT: Synergy Branch
        'bounty_surplus': {
            id: 'bounty_surplus',
            name: 'Surplus Finder',
            description: '10% chance for bonus materials per rank',
            detailedDescription: 'Clients sometimes pay in surplus goods. Chance to receive random crafting materials (bars, hides, cloth) on contract delivery.',
            position: { x: 550, y: 430 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'surplusMaterial', value: 0.10, perRank: true }
            ],
            prerequisites: ['bounty_foundation'],
            tier: 2
        },

        // ==================== TIER 3 ====================
        // LEFT: Golden Touch
        'bounty_golden': {
            id: 'bounty_golden',
            name: 'Golden Touch',
            description: '+20% gold for T4+ contracts',
            detailedDescription: 'High-tier contracts pay significantly better. T4 and T5 contracts grant 20% bonus gold.',
            position: { x: 150, y: 330 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'highTierGoldBonus', value: 0.20, perRank: false }
            ],
            prerequisites: ['bounty_lucrative'],
            tier: 3
        },

        // CENTER: Bulk Processor
        'bounty_swift': {
            id: 'bounty_swift',
            name: 'Bulk Processor',
            description: '+10% progress to other contracts on delivery',
            detailedDescription: 'Completing contracts inspires efficiency. When you deliver a contract, all other active contracts gain 10% bonus progress toward completion.',
            position: { x: 350, y: 330 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'bulkProcessor', value: 0.10, perRank: false }
            ],
            prerequisites: ['bounty_efficient'],
            tier: 3
        },

        // RIGHT: Guild Connections
        'bounty_guild': {
            id: 'bounty_guild',
            name: 'Guild Connections',
            description: '+2% guild assist chance per rank',
            detailedDescription: 'Your ties to the Pilfering Guild grow stronger. Guild members are more likely to assist with your contracts.',
            position: { x: 550, y: 330 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'guildAssistBonus', value: 0.02, perRank: true }
            ],
            prerequisites: ['bounty_surplus'],
            tier: 3
        },

        // ==================== TIER 4 ====================
        // LEFT: Jackpot
        'bounty_jackpot': {
            id: 'bounty_jackpot',
            name: 'Jackpot',
            description: '8% chance for 2x rewards per rank',
            detailedDescription: 'Sometimes contracts pay out more than expected. Chance to receive double gold and XP rewards.',
            position: { x: 150, y: 230 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'doubleContractReward', value: 0.08, perRank: true }
            ],
            prerequisites: ['bounty_golden'],
            tier: 4
        },

        // CENTER: Rapid Completion
        'bounty_rapid': {
            id: 'bounty_rapid',
            name: 'Rapid Completion',
            description: '-10% requirements for most-completed type',
            detailedDescription: 'You specialize in what you do best. Contracts of your most-completed type have reduced requirements.',
            position: { x: 350, y: 230 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'specializedReduction', value: 0.10, perRank: false }
            ],
            prerequisites: ['bounty_swift'],
            tier: 4
        },

        // RIGHT: Reputation Streak
        'bounty_streak': {
            id: 'bounty_streak',
            name: 'Reputation Streak',
            description: '+2% rewards per consecutive delivery (max 20%)',
            detailedDescription: 'Building a reputation as a reliable hunter. Each consecutive contract completion without abandoning adds +2% to all rewards, stacking up to 20%. Abandoning a contract resets the streak.',
            position: { x: 550, y: 230 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'reputationStreak', value: 0.02, perRank: false }
            ],
            prerequisites: ['bounty_guild'],
            tier: 4
        },

        // ==================== TIER 5 - CAPSTONES ====================
        // LEFT: Fortune's Favor
        'bounty_fortune': {
            id: 'bounty_fortune',
            name: "Fortune's Favor",
            description: 'Jackpot can now proc 5x instead of 2x',
            detailedDescription: 'Lady luck smiles upon you. When Jackpot procs, it has a chance to multiply rewards by 5x instead of 2x. Combined with contract costs, this is where you profit.',
            position: { x: 150, y: 130 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'fortuneFavor', value: 1, perRank: false }
            ],
            prerequisites: ['bounty_jackpot'],
            tier: 5
        },

        // CENTER: Mega Contracts
        'bounty_mega': {
            id: 'bounty_mega',
            name: 'Mega Contracts',
            description: 'Unlock bulk contracts (5x requirements, 7x rewards)',
            detailedDescription: 'Accept mega versions of contracts with 5x the requirements but 7x the rewards. Great for idle play and pairs well with Jackpot procs.',
            position: { x: 350, y: 130 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'megaContracts', value: 1, perRank: false }
            ],
            prerequisites: ['bounty_rapid'],
            tier: 5
        },

        // RIGHT: Contract Momentum
        'bounty_momentum': {
            id: 'bounty_momentum',
            name: 'Contract Momentum',
            description: '+15% bonus to contract item/mob while active',
            detailedDescription: 'Active contracts boost your efficiency. While a contract is active and not yet complete, gain +15% yield/drops/speed for that SPECIFIC item or mob only. Bonus ends when requirement is met.',
            position: { x: 550, y: 130 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'contractMomentum', value: 0.15, perRank: false }
            ],
            prerequisites: ['bounty_streak'],
            tier: 5
        },

        // ==================== TIER 6 - SUPER CAPSTONE ====================
        'bounty_dedicated': {
            id: 'bounty_dedicated',
            name: 'Dedicated Supplier',
            description: 'Pin 1 contract to always reappear after delivery',
            detailedDescription: 'Choose one contract to "pin" - it will always reappear in available contracts after you deliver it. Enables true idle farming by guaranteeing your preferred contract is always available.',
            position: { x: 350, y: 50 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'dedicatedSupplier', value: 1, perRank: false }
            ],
            prerequisites: ['bounty_fortune', 'bounty_mega', 'bounty_momentum'],
            requirePrerequisites: 2,
            tier: 6
        }
    },

    connections: [
        // Tier 1 to Tier 2
        { from: 'bounty_foundation', to: 'bounty_lucrative', style: 'straight' },
        { from: 'bounty_foundation', to: 'bounty_efficient', style: 'straight' },
        { from: 'bounty_foundation', to: 'bounty_surplus', style: 'straight' },

        // Tier 2 to Tier 3
        { from: 'bounty_lucrative', to: 'bounty_golden', style: 'straight' },
        { from: 'bounty_efficient', to: 'bounty_swift', style: 'straight' },
        { from: 'bounty_surplus', to: 'bounty_guild', style: 'straight' },

        // Tier 3 to Tier 4
        { from: 'bounty_golden', to: 'bounty_jackpot', style: 'straight' },
        { from: 'bounty_swift', to: 'bounty_rapid', style: 'straight' },
        { from: 'bounty_guild', to: 'bounty_streak', style: 'straight' },

        // Tier 4 to Tier 5
        { from: 'bounty_jackpot', to: 'bounty_fortune', style: 'straight' },
        { from: 'bounty_rapid', to: 'bounty_mega', style: 'straight' },
        { from: 'bounty_streak', to: 'bounty_momentum', style: 'straight' },

        // Tier 5 to Super Capstone (all connect, but only 2 required)
        { from: 'bounty_fortune', to: 'bounty_dedicated', style: 'straight' },
        { from: 'bounty_mega', to: 'bounty_dedicated', style: 'straight' },
        { from: 'bounty_momentum', to: 'bounty_dedicated', style: 'straight' }
    ]
};

// ========================================
// FARMING TALENT TREE (Multi-Path Design)
// ========================================
export const FARMING_TREE = {
    id: SKILLS.FARMING,
    name: 'Farming',
    shape: 'plant',
    canvasSize: { width: 700, height: 550 },
    backgroundColor: '#1a1a2e',
    accentColor: '#228B22', // Forest green for farming theme

    // Multi-path design: ~9 ranks to reach any capstone (level 45-50)
    totalRanks: 21,

    nodes: {
        // ==================== TIER 1 - FOUNDATION ====================
        'farming_foundation': {
            id: 'farming_foundation',
            name: "Farmer's Wisdom",
            description: '+5% Farming XP per rank',
            detailedDescription: 'Your understanding of the land deepens. Each rank increases experience gained from all farming activities.',
            position: { x: 350, y: 480 },
            maxRank: 3,
            effects: [
                { type: 'xpBonus', value: 0.05, perRank: true }
            ],
            prerequisites: [],
            tier: 1
        },

        // ==================== TIER 2 - THREE PATHS ====================
        'farming_growth': {
            id: 'farming_growth',
            name: 'Growth Accelerator',
            description: '-10% growth time per rank',
            detailedDescription: 'Your crops grow faster under your care. Reduces growth time for all planted crops, herbs, and trees.',
            position: { x: 180, y: 380 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'growthTimeReduction', value: 0.10, perRank: true }
            ],
            prerequisites: ['farming_foundation'],
            tier: 2
        },
        'farming_seeds': {
            id: 'farming_seeds',
            name: 'Seed Savant',
            description: '+15% seed drops from raking per rank',
            detailedDescription: 'You have a knack for finding seeds. Increases seed drop rate when raking the grass patch.',
            position: { x: 350, y: 380 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'seedDropBonus', value: 0.15, perRank: true }
            ],
            prerequisites: ['farming_foundation'],
            tier: 2
        },
        'farming_harvest': {
            id: 'farming_harvest',
            name: 'Bountiful Harvest',
            description: '+10% crop yield per rank',
            detailedDescription: 'Your harvests are more plentiful. Increases the quantity of items received when harvesting any plant.',
            position: { x: 520, y: 380 },
            maxRank: 2,
            effects: [
                { type: 'lootBonus', value: 0.10, perRank: true }
            ],
            prerequisites: ['farming_foundation'],
            tier: 2
        },

        // ==================== TIER 3 - SPECIALIZATIONS ====================
        'farming_herbalist': {
            id: 'farming_herbalist',
            name: "Herbalist's Touch",
            description: '+25% herb yield',
            detailedDescription: 'You specialize in herb cultivation. Significantly increases yield when harvesting herbs.',
            position: { x: 180, y: 280 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'herbYieldBonus', value: 0.25, perRank: false }
            ],
            prerequisites: ['farming_growth'],
            tier: 3
        },
        'farming_arborist': {
            id: 'farming_arborist',
            name: "Arborist's Knowledge",
            description: '+25% tree yield, -10% tree growth time',
            detailedDescription: 'You specialize in tree cultivation. Trees yield more logs and grow faster.',
            position: { x: 350, y: 280 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'treeYieldBonus', value: 0.25, perRank: false },
                { type: 'flatBonus', bonus: 'treeGrowthReduction', value: 0.10, perRank: false }
            ],
            prerequisites: ['farming_seeds'],
            tier: 3
        },
        'farming_cropmaster': {
            id: 'farming_cropmaster',
            name: 'Crop Master',
            description: '+25% crop yield',
            detailedDescription: 'You specialize in crop cultivation. Significantly increases yield when harvesting crops.',
            position: { x: 520, y: 280 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'cropYieldBonus', value: 0.25, perRank: false }
            ],
            prerequisites: ['farming_harvest'],
            tier: 3
        },

        // ==================== TIER 4 - PRE-CAPSTONES ====================
        'farming_haste': {
            id: 'farming_haste',
            name: "Nature's Haste",
            description: '8% chance for 50% faster growth per rank',
            detailedDescription: 'Sometimes nature blesses your plants. Chance when planting for crops to complete in half the normal time.',
            position: { x: 180, y: 180 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'fastGrowth', value: 0.08, perRank: true }
            ],
            prerequisites: ['farming_herbalist'],
            tier: 4
        },
        'farming_second': {
            id: 'farming_second',
            name: 'Second Harvest',
            description: '12% chance for double harvest per rank',
            detailedDescription: 'Sometimes crops yield twice. Chance when harvesting to receive the harvest again without clearing the plot.',
            position: { x: 350, y: 180 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'secondHarvest', value: 0.12, perRank: true }
            ],
            prerequisites: ['farming_arborist'],
            tier: 4
        },
        'farming_reclaim': {
            id: 'farming_reclaim',
            name: 'Seed Reclamation',
            description: '15% chance to return seed on harvest per rank',
            detailedDescription: 'Your careful harvesting sometimes preserves seeds. Chance to receive the planted seed back when harvesting. <span class="pet-synergy"><span class="husbandry-icon"></span> Your pets bring back extra seeds.</span>',
            position: { x: 520, y: 180 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'seedReclaim', value: 0.15, perRank: true }
            ],
            prerequisites: ['farming_cropmaster'],
            tier: 4
        },

        // ==================== TIER 5 - CAPSTONES (Choose One Path) ====================
        'farming_verdant': {
            id: 'farming_verdant',
            name: 'Verdant Blessing',
            description: '-25% all growth, 5% instant completion',
            detailedDescription: 'The land itself favors you. All growth times are reduced by 25%, and plants have a 5% chance to complete instantly when planted.',
            position: { x: 180, y: 80 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'verdantGrowthReduction', value: 0.25, perRank: false },
                { type: 'procChance', proc: 'instantGrowth', value: 0.05, perRank: false }
            ],
            prerequisites: ['farming_haste'],
            tier: 5
        },
        'farming_abundant': {
            id: 'farming_abundant',
            name: 'Abundant Fields',
            description: '+40% all yields, 15% bonus seed',
            detailedDescription: 'Your fields overflow with bounty. All harvests yield 40% more, and have a 15% chance to drop a bonus seed of that plant type. <span class="pet-synergy"><span class="husbandry-icon"></span> Your pets have increased seed drop chance.</span>',
            position: { x: 350, y: 80 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'abundantYieldBonus', value: 0.40, perRank: false },
                { type: 'procChance', proc: 'bonusSeedDrop', value: 0.15, perRank: false }
            ],
            prerequisites: ['farming_second'],
            tier: 5
        },
        'farming_eternal': {
            id: 'farming_eternal',
            name: 'Eternal Garden',
            description: 'Auto-replant, 20% free seeds',
            detailedDescription: 'Your garden tends itself. Plots automatically replant the same crop after harvest (if seeds available), and you have a 20% chance to not consume seeds when planting.',
            position: { x: 520, y: 80 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'autoReplant', value: 1, perRank: false },
                { type: 'procChance', proc: 'freeSeed', value: 0.20, perRank: false }
            ],
            prerequisites: ['farming_reclaim'],
            tier: 5
        }
    },

    connections: [
        // Tier 1 to Tier 2
        { from: 'farming_foundation', to: 'farming_growth', style: 'straight' },
        { from: 'farming_foundation', to: 'farming_seeds', style: 'straight' },
        { from: 'farming_foundation', to: 'farming_harvest', style: 'straight' },

        // Tier 2 to Tier 3
        { from: 'farming_growth', to: 'farming_herbalist', style: 'straight' },
        { from: 'farming_seeds', to: 'farming_arborist', style: 'straight' },
        { from: 'farming_harvest', to: 'farming_cropmaster', style: 'straight' },

        // Tier 3 to Tier 4
        { from: 'farming_herbalist', to: 'farming_haste', style: 'straight' },
        { from: 'farming_arborist', to: 'farming_second', style: 'straight' },
        { from: 'farming_cropmaster', to: 'farming_reclaim', style: 'straight' },

        // Tier 4 to Tier 5 (Capstones)
        { from: 'farming_haste', to: 'farming_verdant', style: 'straight' },
        { from: 'farming_second', to: 'farming_abundant', style: 'straight' },
        { from: 'farming_reclaim', to: 'farming_eternal', style: 'straight' }
    ]
};

/**
 * Architecting Talent Tree
 * Shape: Building/tower structure
 *
 * Multi-path design with 3 distinct capstones:
 * - Blueprint Efficiency: Build speed and auto-queue
 * - Civic Benefactor: Contract rewards and spawn rates
 * - Town Integration: Passive XP from unlocked milestone systems
 *
 * Layout:
 *     [AssemblyLine]    [GrandArchitect]    [RealmSteward]
 *           |                  |                  |
 *      [AutoQueue]      [TrustedBuilder]    [BeastKeeper]
 *           |                  |                  |
 *   [MaterialMastery]  [ValuedContractor]  [GuildLiaison]
 *           |                  |                  |
 *     [QuickHands]       [CivicPride]    [KitchenOverseer]
 *            \                |                /
 *                    [BuilderFoundation]
 */
export const ARCHITECTING_TREE = {
    id: SKILLS.ARCHITECTING,
    name: 'Architecting',
    shape: 'building',
    canvasSize: { width: 700, height: 550 },
    backgroundColor: '#1a1a2e',
    accentColor: '#CD853F', // Peru/wood brown for building theme

    // Multi-path design: ~9 ranks to reach any capstone (level 45-50)
    totalRanks: 31,

    nodes: {
        // ==================== TIER 1 - FOUNDATION ====================
        'arch_foundation': {
            id: 'arch_foundation',
            name: "Builder's Foundation",
            description: '+5% Architecting XP per rank',
            detailedDescription: 'Your understanding of construction deepens. Each rank increases experience gained from all architecting activities.',
            position: { x: 350, y: 480 },
            maxRank: 3,
            effects: [
                { type: 'xpBonus', value: 0.05, perRank: true }
            ],
            prerequisites: [],
            tier: 1
        },

        // ==================== TIER 2 - THREE PATHS ====================
        'arch_quick': {
            id: 'arch_quick',
            name: 'Quick Hands',
            description: '+5% blueprint build speed per rank',
            detailedDescription: 'Your practiced hands work faster. Reduces the time needed to complete blueprint commissions.',
            position: { x: 180, y: 380 },
            maxRank: 3,
            effects: [
                { type: 'speedBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['arch_foundation'],
            tier: 2
        },
        'arch_civic': {
            id: 'arch_civic',
            name: 'Civic Pride',
            description: '+10% gold from civic contracts per rank',
            detailedDescription: 'Your reputation brings better pay. Increases the gold reward from completing civic contract projects.',
            position: { x: 350, y: 380 },
            maxRank: 3,
            effects: [
                { type: 'flatBonus', bonus: 'contractGoldBonus', value: 0.10, perRank: true }
            ],
            prerequisites: ['arch_foundation'],
            tier: 2
        },
        'arch_kitchen': {
            id: 'arch_kitchen',
            name: 'Kitchen Overseer',
            description: '+15/25/40 Architecting XP on auto-eat',
            detailedDescription: 'Overseeing the Mess Hall teaches you about efficient design. Earn Architecting XP each time auto-eat consumes food. Requires Mess Hall milestone.',
            position: { x: 520, y: 380 },
            maxRank: 3,
            effects: [
                { type: 'flatBonus', bonus: 'autoEatXP', value: 15, perRank: 'scaling', scaling: [15, 25, 40] }
            ],
            prerequisites: ['arch_foundation'],
            tier: 2
        },

        // ==================== TIER 3 - SPECIALIZATIONS ====================
        'arch_material': {
            id: 'arch_material',
            name: 'Material Mastery',
            description: '5% chance to preserve materials per rank',
            detailedDescription: 'Efficient use of materials reduces waste. Chance to not consume materials when completing a blueprint.',
            position: { x: 180, y: 280 },
            maxRank: 3,
            effects: [
                { type: 'procChance', proc: 'preserveMaterials', value: 0.05, perRank: true }
            ],
            prerequisites: ['arch_quick'],
            tier: 3
        },
        'arch_valued': {
            id: 'arch_valued',
            name: 'Valued Contractor',
            description: '+5% civic contract XP per rank',
            detailedDescription: 'Your expertise is recognized. Increases the experience reward from completing civic contracts.',
            position: { x: 350, y: 280 },
            maxRank: 3,
            effects: [
                { type: 'flatBonus', bonus: 'contractXPBonus', value: 0.05, perRank: true }
            ],
            prerequisites: ['arch_civic'],
            tier: 3
        },
        'arch_guild': {
            id: 'arch_guild',
            name: 'Guild Liaison',
            description: '+20/35/50 Architecting XP on guild chest claim',
            detailedDescription: 'Working with the Pilfering Guild expands your architectural knowledge. Earn Architecting XP when claiming from the guild chest. Requires Guild Hall milestone.',
            position: { x: 520, y: 280 },
            maxRank: 3,
            effects: [
                { type: 'flatBonus', bonus: 'guildChestXP', value: 20, perRank: 'scaling', scaling: [20, 35, 50] }
            ],
            prerequisites: ['arch_kitchen'],
            tier: 3
        },

        // ==================== TIER 4 - PRE-CAPSTONES ====================
        'arch_autoqueue': {
            id: 'arch_autoqueue',
            name: 'Auto-Queue',
            description: 'Contractor auto-starts next blueprint',
            detailedDescription: 'Your contractor knows the drill. When a blueprint finishes, he automatically starts the next one of the same tier — no downtime, no nagging.',
            position: { x: 180, y: 180 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'autoQueueBlueprint', value: 1, perRank: false }
            ],
            prerequisites: ['arch_material'],
            tier: 4
        },
        'arch_trusted': {
            id: 'arch_trusted',
            name: 'Trusted Builder',
            description: '+2% civic contract spawn chance per rank',
            detailedDescription: 'Your reputation precedes you. Increases the chance for a new civic contract to become available when completing blueprints or milestones.',
            position: { x: 350, y: 180 },
            maxRank: 3,
            effects: [
                { type: 'flatBonus', bonus: 'contractSpawnBonus', value: 0.02, perRank: true }
            ],
            prerequisites: ['arch_valued'],
            tier: 4
        },
        'arch_beast': {
            id: 'arch_beast',
            name: 'Beast Keeper',
            description: '+20/35/50 Architecting XP on pet chest claim',
            detailedDescription: 'Managing the Pet Quarters teaches efficient space usage. Earn Architecting XP when claiming from the pet loot chest. Requires Pet Quarters milestone.',
            position: { x: 520, y: 180 },
            maxRank: 3,
            effects: [
                { type: 'flatBonus', bonus: 'petChestXP', value: 20, perRank: 'scaling', scaling: [20, 35, 50] }
            ],
            prerequisites: ['arch_guild'],
            tier: 4
        },

        // ==================== TIER 5 - CAPSTONES (Choose One Path) ====================
        'arch_assembly': {
            id: 'arch_assembly',
            name: 'Apprentice Contractor',
            description: 'Run 2 contractor tasks at 75% speed each',
            detailedDescription: 'Your contractor has taken on an eager apprentice. They can juggle two jobs at once — any combination of blueprints and milestones — though he has to keep one eye on the kid, slowing both tasks to 75% speed.',
            position: { x: 180, y: 80 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'dualBlueprint', value: 1, perRank: false }
            ],
            prerequisites: ['arch_autoqueue'],
            tier: 5
        },
        'arch_grand': {
            id: 'arch_grand',
            name: 'Grand Architect',
            description: 'Civic contracts are always Rare+',
            detailedDescription: 'Your reputation attracts only the most prestigious projects. Civic contracts can no longer spawn as Uncommon - they will always be Rare or Legendary, with greatly increased odds of Legendary contracts.',
            position: { x: 350, y: 80 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'contractRarityBoost', value: 1, perRank: false }
            ],
            prerequisites: ['arch_trusted'],
            tier: 5
        },
        'arch_realm': {
            id: 'arch_realm',
            name: 'Realm Steward',
            description: '+50% passive XP, BloomHold grants XP',
            detailedDescription: 'You oversee all town infrastructure. All milestone-unlocked passive XP bonuses are increased by 50%, and BloomHold plant/harvest operations grant +10 Architecting XP.',
            position: { x: 520, y: 80 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'passiveXPBonus', value: 0.50, perRank: false },
                { type: 'flatBonus', bonus: 'bloomholdXP', value: 10, perRank: false }
            ],
            prerequisites: ['arch_beast'],
            tier: 5
        }
    },

    connections: [
        // Tier 1 to Tier 2
        { from: 'arch_foundation', to: 'arch_quick', style: 'straight' },
        { from: 'arch_foundation', to: 'arch_civic', style: 'straight' },
        { from: 'arch_foundation', to: 'arch_kitchen', style: 'straight' },

        // Tier 2 to Tier 3
        { from: 'arch_quick', to: 'arch_material', style: 'straight' },
        { from: 'arch_civic', to: 'arch_valued', style: 'straight' },
        { from: 'arch_kitchen', to: 'arch_guild', style: 'straight' },

        // Tier 3 to Tier 4
        { from: 'arch_material', to: 'arch_autoqueue', style: 'straight' },
        { from: 'arch_valued', to: 'arch_trusted', style: 'straight' },
        { from: 'arch_guild', to: 'arch_beast', style: 'straight' },

        // Tier 4 to Tier 5 (Capstones)
        { from: 'arch_autoqueue', to: 'arch_assembly', style: 'straight' },
        { from: 'arch_trusted', to: 'arch_grand', style: 'straight' },
        { from: 'arch_beast', to: 'arch_realm', style: 'straight' }
    ]
};

/**
 * Combat Talent Tree
 * Shape: Tree with trunk and 4 branches (literal tree shape)
 *
 * Multi-path design with 4 distinct capstones:
 * - Melee (upper-left): Warrior damage and execute mechanics
 * - Survival (lower-left): Defense, HP, and sustain
 * - Ranged (upper-right): Arrow damage and multi-shot
 * - Magic (lower-right): Spell damage and elemental synergy
 *
 * Layout:
 *     [Berserker]                                    [ArrowStorm]
 *          |                                              |
 *     [Whirlwind]                                    [MultiShot]
 *          |                                              |
 *    [Executioner]                                [BarbedArrows]
 *          |                                              |
 *    [DeepWounds]                                  [AmmoConserve]
 *          |                                              |
 *   [Relentless]                                    [SteadyAim]
 *          |                                              |
 *    [BladeMastery]                                [ArchersEye]
 *           \                                            /
 *        [PhysicalProwess]                    [PrecisionTraining]
 *                 \                                  /
 *                      [CombatReflexes]
 *                            |
 *                      [BattleHardened]
 *                            |
 *                    [WarriorFoundation]
 *                 /                                  \
 *        [PhysicalProwess]                    [PrecisionTraining]
 *           /                                            \
 *      [IronSkin]                                [ArcaneAffinity]
 *          |                                              |
 *     [VitalForce]                               [SpellEfficiency]
 *          |                                              |
 *    [CombatMedic]                              [ElemMastery]
 *          |                                              |
 *   [Regeneration]                               [SpellWeaver]
 *          |                                              |
 *    [ThornArmor]                               [ElemCascade]
 *          |                                              |
 *     [LastStand]                                 [SpellEcho]
 */
export const COMBAT_TREE = {
    id: SKILLS.COMBAT,
    name: 'Combat',
    shape: 'tree',
    canvasSize: { width: 850, height: 1100 },
    backgroundColor: '#1a1a2e',
    accentColor: '#8B0000', // Dark red for combat

    // 29 nodes, 49 total ranks - larger tree for late-game focus
    totalRanks: 49,

    nodes: {
        // ==================== TRUNK (Core Combat - Everyone Takes) ====================
        'combat_foundation': {
            id: 'combat_foundation',
            name: "Warrior's Foundation",
            description: '+5% Combat XP per rank',
            detailedDescription: 'The foundation of all combat prowess. Each rank increases experience gained from defeating enemies.',
            position: { x: 425, y: 760 },
            maxRank: 3,
            effects: [
                { type: 'xpBonus', value: 0.05, perRank: true }
            ],
            prerequisites: [],
            tier: 1
        },
        'combat_hardened': {
            id: 'combat_hardened',
            name: 'Battle Hardened',
            description: '+3% all damage per rank',
            detailedDescription: 'Your strikes become more powerful. Increases damage dealt by all attack styles.',
            position: { x: 425, y: 670 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'allDamage', value: 0.03, perRank: true }
            ],
            prerequisites: ['combat_foundation'],
            tier: 2
        },
        'combat_reflexes': {
            id: 'combat_reflexes',
            name: 'Combat Reflexes',
            description: '-5% attack cooldown per rank',
            detailedDescription: 'Your combat instincts sharpen. Attack faster with all weapon types.',
            position: { x: 425, y: 580 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'attackSpeed', value: 0.05, perRank: true }
            ],
            prerequisites: ['combat_hardened'],
            tier: 3
        },

        // ==================== BRANCH JUNCTIONS ====================
        'combat_physical': {
            id: 'combat_physical',
            name: 'Physical Prowess',
            description: '+5% melee damage and defense per rank',
            detailedDescription: 'Your physical conditioning improves. Boosts both melee damage output and defensive capabilities.',
            position: { x: 240, y: 510 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'meleeDamage', value: 0.05, perRank: true },
                { type: 'flatBonus', bonus: 'defense', value: 0.05, perRank: true }
            ],
            prerequisites: ['combat_reflexes'],
            tier: 4
        },
        'combat_precision': {
            id: 'combat_precision',
            name: 'Precision Training',
            description: '+5% accuracy and spell damage per rank',
            detailedDescription: 'Your aim and focus improve. Boosts both ranged accuracy and magical power.',
            position: { x: 610, y: 510 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'accuracy', value: 0.05, perRank: true },
                { type: 'flatBonus', bonus: 'spellDamage', value: 0.05, perRank: true }
            ],
            prerequisites: ['combat_reflexes'],
            tier: 4
        },

        // ==================== MELEE BRANCH (Upper-Left) ====================
        'combat_blade': {
            id: 'combat_blade',
            name: 'Blade Mastery',
            description: '+7% melee damage per rank',
            detailedDescription: 'Your skill with melee weapons grows. Significantly increases damage with swords, dirks, and greatswords.',
            position: { x: 175, y: 435 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'meleeDamage', value: 0.07, perRank: true }
            ],
            prerequisites: ['combat_physical'],
            tier: 5
        },
        'combat_relentless': {
            id: 'combat_relentless',
            name: 'Relentless Assault',
            description: '+5% melee attack speed per rank',
            detailedDescription: 'Your melee strikes come faster. Reduces attack cooldown when using melee weapons.',
            position: { x: 130, y: 360 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'meleeSpeed', value: 0.05, perRank: true }
            ],
            prerequisites: ['combat_blade'],
            tier: 6
        },
        'combat_deepwounds': {
            id: 'combat_deepwounds',
            name: 'Deep Wounds',
            description: 'Melee attacks have a 35% chance to apply bleed (5 dmg/tick × 5 ticks) per rank',
            detailedDescription: 'Your strikes leave lasting wounds. Melee attacks have a 35% chance to apply a bleeding effect that deals damage over time. Higher damage per application rewards attack speed and extra hit sources.',
            position: { x: 100, y: 285 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'meleeBleed', value: 5, perRank: true },
                { type: 'flatBonus', bonus: 'meleeBleedChance', value: 0.35 }
            ],
            prerequisites: ['combat_relentless'],
            tier: 7
        },
        'combat_executioner': {
            id: 'combat_executioner',
            name: 'Executioner',
            description: '+25% damage to enemies below 25% HP',
            detailedDescription: 'You excel at finishing wounded foes. Deal significantly more damage to enemies at low health.',
            position: { x: 80, y: 210 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'executeBonus', value: 0.25, perRank: false }
            ],
            prerequisites: ['combat_deepwounds'],
            tier: 8
        },
        'combat_whirlwind': {
            id: 'combat_whirlwind',
            name: 'Whirlwind',
            description: '15% chance to strike twice per attack',
            detailedDescription: 'Your blade becomes a blur. Melee attacks have a chance to hit twice in rapid succession.',
            position: { x: 65, y: 135 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'doubleStrike', value: 0.15, perRank: false }
            ],
            prerequisites: ['combat_executioner'],
            tier: 9
        },
        'combat_berserker': {
            id: 'combat_berserker',
            name: "Berserker's Rampage",
            description: 'Kills grant +10% damage for 30s (stacks 5×)',
            detailedDescription: 'Each kill fuels your fury. Defeating enemies grants a stacking damage bonus that rewards aggressive play.',
            position: { x: 55, y: 60 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'berserkerRampage', value: 1, perRank: false }
            ],
            prerequisites: ['combat_whirlwind'],
            tier: 10
        },

        // ==================== SURVIVAL BRANCH (Lower-Left) ====================
        'combat_iron': {
            id: 'combat_iron',
            name: 'Iron Skin',
            description: '+5% defense per rank',
            detailedDescription: 'Your body becomes more resilient. Increases damage reduction from all sources.',
            position: { x: 175, y: 585 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'defense', value: 0.05, perRank: true }
            ],
            prerequisites: ['combat_physical'],
            tier: 5
        },
        'combat_vital': {
            id: 'combat_vital',
            name: 'Vital Force',
            description: '+5 max HP per rank',
            detailedDescription: 'Your vitality increases. Gain additional maximum hit points.',
            position: { x: 130, y: 660 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'maxHp', value: 5, perRank: true }
            ],
            prerequisites: ['combat_iron'],
            tier: 6
        },
        'combat_medic': {
            id: 'combat_medic',
            name: 'Combat Medic',
            description: '+25% food healing per rank',
            detailedDescription: 'You extract more benefit from food. Increases the HP restored when eating food.',
            position: { x: 100, y: 735 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'foodHealing', value: 0.25, perRank: true }
            ],
            prerequisites: ['combat_vital'],
            tier: 7
        },
        'combat_regen': {
            id: 'combat_regen',
            name: 'Regeneration',
            description: 'Heal 1 HP every 5 seconds in combat',
            detailedDescription: 'Your body mends itself during battle. Passively recover health while fighting.',
            position: { x: 80, y: 810 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'combatRegen', value: 1, perRank: false }
            ],
            prerequisites: ['combat_medic'],
            tier: 8
        },
        'combat_thorn': {
            id: 'combat_thorn',
            name: 'Thorn Armor',
            description: 'Reflect 10% damage taken per rank',
            detailedDescription: 'Attackers feel your pain. When hit, deal a portion of the damage back to the attacker.',
            position: { x: 65, y: 885 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'thornDamage', value: 0.10, perRank: true }
            ],
            prerequisites: ['combat_regen'],
            tier: 9
        },
        'combat_laststand': {
            id: 'combat_laststand',
            name: 'Last Stand',
            description: 'Survive fatal hit at 1 HP (once per fight)',
            detailedDescription: 'Death itself cannot claim you easily. When you would take fatal damage, survive with 1 HP instead. Resets each fight. (Online only)',
            position: { x: 55, y: 960 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'lastStand', value: 1, perRank: false }
            ],
            prerequisites: ['combat_thorn'],
            tier: 10
        },

        // ==================== RANGED BRANCH (Upper-Right) ====================
        'combat_archer': {
            id: 'combat_archer',
            name: "Archer's Eye",
            description: '+7% ranged damage per rank',
            detailedDescription: 'Your aim becomes deadly. Significantly increases damage with bows and arrows.',
            position: { x: 675, y: 435 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'rangedDamage', value: 0.07, perRank: true }
            ],
            prerequisites: ['combat_precision'],
            tier: 5
        },
        'combat_steady': {
            id: 'combat_steady',
            name: 'Steady Aim',
            description: '+10% ranged accuracy per rank',
            detailedDescription: 'Your shots rarely miss. Increases hit chance when using ranged weapons.',
            position: { x: 720, y: 360 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'rangedAccuracy', value: 0.10, perRank: true }
            ],
            prerequisites: ['combat_archer'],
            tier: 6
        },
        'combat_ammo': {
            id: 'combat_ammo',
            name: 'Ammo Conservation',
            description: '10% chance to save ammo per rank',
            detailedDescription: 'Your shots are efficient. Chance to not consume arrows when attacking.',
            position: { x: 750, y: 285 },
            maxRank: 2,
            effects: [
                { type: 'procChance', proc: 'ammoSave', value: 0.10, perRank: true }
            ],
            prerequisites: ['combat_steady'],
            tier: 7
        },
        'combat_barbed': {
            id: 'combat_barbed',
            name: 'Barbed Arrows',
            description: 'Ranged attacks have a 35% chance to apply bleed (4 dmg/tick × 5 ticks) per rank',
            detailedDescription: 'Your arrows leave cruel wounds. Ranged attacks have a 35% chance to apply a bleeding effect that deals damage over time. Higher damage per application rewards attack speed and extra hit sources.',
            position: { x: 770, y: 210 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'rangedBleed', value: 4, perRank: true },
                { type: 'flatBonus', bonus: 'rangedBleedChance', value: 0.35 }
            ],
            prerequisites: ['combat_ammo'],
            tier: 8
        },
        'combat_multishot': {
            id: 'combat_multishot',
            name: 'Multi-Shot',
            description: '20% chance to fire 2 extra arrows (no ammo cost)',
            detailedDescription: 'Your bow becomes a rapid-fire weapon. Chance to fire additional arrows without consuming extra ammunition.',
            position: { x: 785, y: 135 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'multiShot', value: 0.20, perRank: false }
            ],
            prerequisites: ['combat_barbed'],
            tier: 9
        },
        'combat_arrowstorm': {
            id: 'combat_arrowstorm',
            name: 'Arrow Storm',
            description: '10% chance to fire 5 arrows (1 ammo consumed)',
            detailedDescription: 'Unleash a devastating volley. Chance to fire a barrage of arrows for massive damage at minimal ammo cost.',
            position: { x: 795, y: 60 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'arrowStorm', value: 0.10, perRank: false }
            ],
            prerequisites: ['combat_multishot'],
            tier: 10
        },

        // ==================== MAGIC BRANCH (Lower-Right) ====================
        'combat_arcane': {
            id: 'combat_arcane',
            name: 'Arcane Affinity',
            description: '+7% spell damage per rank',
            detailedDescription: 'Your magical power grows. Significantly increases damage dealt by all spells.',
            position: { x: 675, y: 585 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'spellDamage', value: 0.07, perRank: true }
            ],
            prerequisites: ['combat_precision'],
            tier: 5
        },
        'combat_efficiency': {
            id: 'combat_efficiency',
            name: 'Spell Efficiency',
            description: '-15% glyph cost per rank',
            detailedDescription: 'Your spells require less energy. Reduces the number of glyphs consumed when casting.',
            position: { x: 720, y: 660 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'glyphCostReduction', value: 0.15, perRank: true }
            ],
            prerequisites: ['combat_arcane'],
            tier: 6
        },
        'combat_elemental': {
            id: 'combat_elemental',
            name: 'Elemental Mastery',
            description: '+20% elemental weakness bonus per rank',
            detailedDescription: 'You exploit elemental vulnerabilities better. Increases bonus damage when using spells against weak enemies.',
            position: { x: 750, y: 735 },
            maxRank: 2,
            effects: [
                { type: 'flatBonus', bonus: 'weaknessBonus', value: 0.20, perRank: true }
            ],
            prerequisites: ['combat_efficiency'],
            tier: 7
        },
        'combat_weaver': {
            id: 'combat_weaver',
            name: 'Spell Weaver',
            description: '+15% spell effect duration',
            detailedDescription: 'Your magical effects linger longer. Increases duration of DoTs, slows, and other spell effects.',
            position: { x: 770, y: 810 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'spellDuration', value: 0.15, perRank: false }
            ],
            prerequisites: ['combat_elemental'],
            tier: 8
        },
        'combat_cascade': {
            id: 'combat_cascade',
            name: 'Elemental Cascade',
            description: '25% chance to also cast random different element',
            detailedDescription: 'Your spells sometimes trigger elemental echoes. When casting, chance to also fire a spell of a different element at 50% power, applying its effects.',
            position: { x: 785, y: 885 },
            maxRank: 1,
            effects: [
                { type: 'procChance', proc: 'elementalCascade', value: 0.25, perRank: false }
            ],
            prerequisites: ['combat_weaver'],
            tier: 9
        },
        'combat_echo': {
            id: 'combat_echo',
            name: 'Spell Echo',
            description: 'Every 3rd spell casts twice',
            detailedDescription: 'Your magical rhythm creates echoes. Every third spell you cast automatically triggers again at full power.',
            position: { x: 795, y: 960 },
            maxRank: 1,
            effects: [
                { type: 'flatBonus', bonus: 'spellEcho', value: 1, perRank: false }
            ],
            prerequisites: ['combat_cascade'],
            tier: 10
        }
    },

    connections: [
        // Trunk connections
        { from: 'combat_foundation', to: 'combat_hardened', style: 'straight' },
        { from: 'combat_hardened', to: 'combat_reflexes', style: 'straight' },

        // Junction connections
        { from: 'combat_reflexes', to: 'combat_physical', style: 'curved' },
        { from: 'combat_reflexes', to: 'combat_precision', style: 'curved' },

        // Melee branch (from physical, going up-left)
        { from: 'combat_physical', to: 'combat_blade', style: 'curved' },
        { from: 'combat_blade', to: 'combat_relentless', style: 'curved' },
        { from: 'combat_relentless', to: 'combat_deepwounds', style: 'curved' },
        { from: 'combat_deepwounds', to: 'combat_executioner', style: 'curved' },
        { from: 'combat_executioner', to: 'combat_whirlwind', style: 'curved' },
        { from: 'combat_whirlwind', to: 'combat_berserker', style: 'curved' },

        // Survival branch (from physical, going down-left)
        { from: 'combat_physical', to: 'combat_iron', style: 'curved' },
        { from: 'combat_iron', to: 'combat_vital', style: 'curved' },
        { from: 'combat_vital', to: 'combat_medic', style: 'curved' },
        { from: 'combat_medic', to: 'combat_regen', style: 'curved' },
        { from: 'combat_regen', to: 'combat_thorn', style: 'curved' },
        { from: 'combat_thorn', to: 'combat_laststand', style: 'curved' },

        // Ranged branch (from precision, going up-right)
        { from: 'combat_precision', to: 'combat_archer', style: 'curved' },
        { from: 'combat_archer', to: 'combat_steady', style: 'curved' },
        { from: 'combat_steady', to: 'combat_ammo', style: 'curved' },
        { from: 'combat_ammo', to: 'combat_barbed', style: 'curved' },
        { from: 'combat_barbed', to: 'combat_multishot', style: 'curved' },
        { from: 'combat_multishot', to: 'combat_arrowstorm', style: 'curved' },

        // Magic branch (from precision, going down-right)
        { from: 'combat_precision', to: 'combat_arcane', style: 'curved' },
        { from: 'combat_arcane', to: 'combat_efficiency', style: 'curved' },
        { from: 'combat_efficiency', to: 'combat_elemental', style: 'curved' },
        { from: 'combat_elemental', to: 'combat_weaver', style: 'curved' },
        { from: 'combat_weaver', to: 'combat_cascade', style: 'curved' },
        { from: 'combat_cascade', to: 'combat_echo', style: 'curved' }
    ]
};

/**
 * Get talent tree data for a skill
 */
export function getTalentTreeData(skillId) {
    const trees = {
        [SKILLS.MINING]: MINING_TREE,
        [SKILLS.COMBAT]: COMBAT_TREE,
        [SKILLS.SMITHING]: SMITHING_TREE,
        [SKILLS.WOODCUTTING]: WOODCUTTING_TREE,
        [SKILLS.FISHING]: FISHING_TREE,
        [SKILLS.COOKING]: COOKING_TREE,
        [SKILLS.CRAFTING]: CRAFTING_TREE,
        [SKILLS.PYRE]: PYRE_TREE,
        [SKILLS.ALCHEMY]: ALCHEMY_TREE,
        [SKILLS.GATHERER]: GATHERER_TREE,
        [SKILLS.PILFERING]: PILFERING_TREE,
        [SKILLS.GLYPHBINDING]: GLYPHBINDING_TREE,
        [SKILLS.WOODWORKING]: WOODWORKING_TREE,
        [SKILLS.HUSBANDRY]: HUSBANDRY_TREE,
        [SKILLS.BOUNTY]: BOUNTY_TREE,
        [SKILLS.FARMING]: FARMING_TREE,
        [SKILLS.ARCHITECTING]: ARCHITECTING_TREE,
    };

    return trees[skillId] || null;
}

/**
 * Get all available talent trees
 */
export function getAllTalentTrees() {
    return {
        [SKILLS.MINING]: MINING_TREE,
        [SKILLS.COMBAT]: COMBAT_TREE,
        [SKILLS.SMITHING]: SMITHING_TREE,
        [SKILLS.WOODCUTTING]: WOODCUTTING_TREE,
        [SKILLS.FISHING]: FISHING_TREE,
        [SKILLS.COOKING]: COOKING_TREE,
        [SKILLS.CRAFTING]: CRAFTING_TREE,
        [SKILLS.PYRE]: PYRE_TREE,
        [SKILLS.ALCHEMY]: ALCHEMY_TREE,
        [SKILLS.GATHERER]: GATHERER_TREE,
        [SKILLS.PILFERING]: PILFERING_TREE,
        [SKILLS.GLYPHBINDING]: GLYPHBINDING_TREE,
        [SKILLS.WOODWORKING]: WOODWORKING_TREE,
        [SKILLS.HUSBANDRY]: HUSBANDRY_TREE,
        [SKILLS.BOUNTY]: BOUNTY_TREE,
        [SKILLS.FARMING]: FARMING_TREE,
        [SKILLS.ARCHITECTING]: ARCHITECTING_TREE,
    };
}

/**
 * Check if a skill has a talent tree implemented
 */
export function hasTalentTree(skillId) {
    return getTalentTreeData(skillId) !== null;
}
